/**
 * Content Tracker screens (agency surface): deliverables list and item, My Work, backlog board, monthly scope, Daily
 * Grid, dashboard and admin settings. Every status, count and bucket comes from the server (NFR-05); the screens render
 * them and send edits. Strings go through useSurface().t. Status is a tone plus a text label, never colour alone (NFR-18).
 */
import './content.css';
import { Fragment, useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Heading, Text } from '@qm/ui';
import {
  APPROVALS, BACKLOG_BUCKETS, ITEM_STATUSES, MEMBER_ROLES, PLATFORM_STATUSES,
  type BacklogImportReport, type ClientKind, type DeliverableView, type ItemStatus, type MemberRole, type TrackerMe,
} from '@qm/contracts/content';
import type { Translate } from '@qm/i18n';
import { useSurface } from '../kit/context';
import { errorInfo, newIdempotencyKey } from '../kit/http';
import { useAction, useResource, type Resource } from '../kit/resource';
import { Badge, Checkbox, DataTable, EmptyState, Field, Loadable, Notice, Screen, Stat, Tabs, errorMessage, formatWhen, type Column, type Tone } from '../kit/ui';
import {
  contentApi, deliverableQuery, exportHref, FILTER_KEYS,
  type CatalogRow, type CatalogView, type ContentClient, type ContentMember, type PlatformSyncFields, type SocialAccountOption, type DailyGrid, type DeliverableDetail, type Dashboard, type PagedList,
  type ApprovalLink, type ApprovalLinkCreated, type ApprovalLinkCreateBody, type BacklogBoard, type ImportBody, type ImportReport, type NotificationKind, type NotificationList, type NotificationRow, type ScopeReport, type ScopeRow, type SavedFilter, type Settings, type TrendReport,
  type MyWhatsApp, type WhatsAppLanguage, NOTIFICATION_KINDS,
} from '../api/content';
import { fileHref } from '../api/content';
import { LibraryPicker } from './content-library';

// ---------------------------------------------------------------- pure helpers (exported for tests)

export type Filters = Record<string, string>;
/** The filters shown in the bar; other URL filters (from dashboards and grids) are listed with a clear action. */
const BAR_KEYS = ['client', 'am', 'assignee', 'type', 'platform', 'status', 'backlog', 'month', 'kind', 'q'] as const;
export const GRID_DAYS = 14;

export function filtersFromParams(sp: URLSearchParams): Filters {
  const out: Filters = {};
  for (const k of FILTER_KEYS) {
    const v = sp.get(k);
    if (v) out[k] = v;
  }
  return out;
}

/** Returns a new URLSearchParams with one key set (or removed when the value is empty). */
export function setParam(sp: URLSearchParams, key: string, value: string): URLSearchParams {
  const next = new URLSearchParams(sp);
  if (value) next.set(key, value);
  else next.delete(key);
  return next;
}

/** The list URL for a set of filters. Every link in the content tracker goes through here. */
export function listHref(filters: Filters): string {
  const qs = new URLSearchParams();
  for (const k of FILTER_KEYS) if (filters[k]) qs.set(k, filters[k]);
  const s = qs.toString();
  return s ? `/content?${s}` : '/content';
}

export const listKey = (filters: Filters) => `content:list:${listHref(filters)}`;
export const gridKey = (from: string) => `content:grid:${from}:${GRID_DAYS}`;
export const dashKey = (month: string, all: boolean) => `content:dash:${month}:${all ? 'all' : 'mine'}`;

const TONE: Partial<Record<ItemStatus, Tone>> = {
  posted: 'good', approved: 'good', delivered: 'good',
  revision_needed: 'bad', overdue: 'bad', partial_overdue: 'bad',
  at_risk: 'warn', partially_posted: 'warn',
  due_today: 'accent',
};
/** Colour for a status. The label always comes with it (NFR-18). */
export const statusTone = (s: ItemStatus): Tone => TONE[s] ?? 'neutral';

const DAY = 86_400_000;
export function shiftDay(iso: string, n: number): string {
  return new Date(Date.parse(`${iso}T00:00:00Z`) + n * DAY).toISOString().slice(0, 10);
}
export function shiftMonth(month: string, n: number): string {
  const y = Number(month.slice(0, 4));
  const m = Number(month.slice(5, 7)) - 1 + n;
  return new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 7);
}
const dateTag = (locale: string) => (locale === 'ar' ? 'ar-AE-u-nu-latn' : 'en-AE');

/** NFR-21: dates shown as dd-MMM-yy. */
export function fmtDate(locale: string, iso: string | null | undefined): string {
  if (!iso) return '';
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  const mon = new Intl.DateTimeFormat(dateTag(locale), { month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
  return `${String(d).padStart(2, '0')}-${mon}-${String(y).slice(-2)}`;
}
export function weekdayOf(locale: string, iso: string): string {
  const [y = 0, m = 1, d = 1] = iso.split('-').map(Number);
  return new Intl.DateTimeFormat(dateTag(locale), { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, d)));
}
/** Month label: "Oct 26" for column headers, "Oct 2026" when `long` (sentences). */
export function fmtMonth(locale: string, month: string, long = false): string {
  const [y = 0, m = 1] = month.split('-').map(Number);
  return new Intl.DateTimeFormat(dateTag(locale), { month: 'short', year: long ? 'numeric' : '2-digit', timeZone: 'UTC' }).format(new Date(Date.UTC(y, m - 1, 1)));
}

/** RFC 4180 CSV to row objects keyed by the header row (quoted fields, CRLF, BOM). Blank lines are skipped. */
export function parseCsv(text: string): Record<string, string>[] {
  const src = text.replace(/^\uFEFF/, '');
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i]!;
    if (quoted) {
      if (c === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; } else quoted = false;
      } else cell += c;
      continue;
    }
    if (c === '"') quoted = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += c;
  }
  if (cell !== '' || row.length > 0) { row.push(cell); rows.push(row); }
  const [head = [], ...body] = rows.filter((r) => r.some((c) => c.trim() !== ''));
  const keys = head.map((h) => h.trim());
  return body.map((r) => Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? '').trim()]).filter(([k]) => k !== '')));
}

/** A version conflict gets its own message (NFR-16); other errors use the kit mapping. */
export function contentErrorMessage(t: Translate, err: unknown): string {
  if (errorInfo(err).reason === 'version_conflict') return t('portal.partners.content.error.versionConflict');
  return errorMessage(t, err);
}

const catalogLabel = (rows: readonly CatalogRow[], key: string) => rows.find((r) => r.key === key)?.label ?? key;

/** US-15: sync error codes the portal knows how to explain. Anything else gets the generic line (codes never hold secrets). */
const SYNC_ERROR_KEYS: Record<string, string> = {
  account_not_found: 'account_not_found', account_disconnected: 'account_disconnected', token_invalid: 'token_invalid',
  credentials_unavailable: 'credentials_unavailable', permission_denied: 'permission_denied', network_error: 'network_error',
};
export function syncErrorKey(code: string): string {
  if (code.startsWith('vault_')) return 'credentials_unavailable';
  return SYNC_ERROR_KEYS[code] ?? 'generic';
}
/** Platform names as the platforms print them (brand names are not translated). */
const PROVIDER_NAMES: Record<SocialAccountOption['provider'], string> = { facebook: 'Facebook', instagram: 'Instagram', linkedin: 'LinkedIn', youtube: 'YouTube' };
const isHttps = (u: string) => /^https:\/\//i.test(u.trim());

/**
 * Whether a PATCH needs `platforms`. The API checks every submitted platform key against the active catalog and refuses
 * to drop a posted or missed one, so the form sends the list only when the selection moved.
 */
export function platformsChanged(current: readonly string[], picked: readonly string[]): boolean {
  return current.length !== picked.length || picked.some((k) => !current.includes(k));
}

/** Platforms a "Mark posted" pick can include: every platform of the item that is not posted yet (planned, scheduled or missed). */
export function postablePlatforms(platforms: readonly { platform: string; status: string }[]): string[] {
  return platforms.filter((p) => p.status !== 'posted').map((p) => p.platform);
}

/** The result line after a generate run (US-06): none, one or many drafts. */
export function generatedMessage(t: Translate, created: number): string {
  if (created === 0) return t('portal.partners.content.scope.generate.none');
  return t(created === 1 ? 'portal.partners.content.scope.generate.created.one' : 'portal.partners.content.scope.generate.created.other', { n: created });
}

export const NOTIFICATIONS_KEY = 'content:notifications';

// ---------------------------------------------------------------- hooks and shared pieces

export function useApi() {
  const { http } = useSurface();
  return useMemo(() => contentApi(http), [http]);
}

/** Calls `fn` every `ms` while the page is visible (NFR-04: other users see changes within 5 s). */
export function usePoll(fn: () => void, ms: number) {
  useEffect(() => {
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState === 'visible') fn();
    }, ms);
    return () => clearInterval(id);
  }, [fn, ms]);
}

export interface Base { me: TrackerMe; clients: ContentClient[]; members: ContentMember[]; catalog: CatalogView }
export const BASE_KEY = 'content:base';

/** Loads the caller's tracker context once (me, clients, team, catalog) and hands it to the page. */
export function WithBase({ children }: { children: (b: Base, reloadBase: () => void) => ReactNode }) {
  const api = useApi();
  const base = useResource<Base>(BASE_KEY, async () => {
    const [me, clients, members, catalog] = await Promise.all([api.me(), api.clients(), api.members(), api.catalog()]);
    return { me, clients, members, catalog };
  });
  return <Loadable resource={base}>{(b) => children(b, base.reload)}</Loadable>;
}

export function ContentError({ error }: { error: unknown }) {
  const { t } = useSurface();
  if (!error) return null;
  return <div className="qx-alert" role="alert" data-testid="content-error">{contentErrorMessage(t, error)}</div>;
}

export function ContentNav({ me }: { me: TrackerMe }) {
  const { t } = useSurface();
  const links: [string, string][] = [
    ['/content', t('portal.partners.content.nav.list')],
    ['/content/calendar', t('portal.partners.content.nav.calendar')],
    ['/content/my-work', t('portal.partners.content.nav.myWork')],
    ['/content/backlog', t('portal.partners.content.nav.backlog')],
    ['/content/scope', t('portal.partners.content.nav.scope')],
    ['/content/grid', t('portal.partners.content.nav.grid')],
    ['/content/dashboard', t('portal.partners.content.nav.dashboard')],
    ['/content/notifications', t('portal.partners.content.nav.notifications')],
  ];
  if (me.can.setup) links.push(['/content/settings', t('portal.partners.content.nav.settings')]);
  return (
    <nav className="qx-nav qc-nav" aria-label={t('portal.partners.content.nav.label')}>
      {links.map(([to, label]) => <NavLink key={to} to={to} end={to === '/content'}>{label}</NavLink>)}
    </nav>
  );
}

export function StatusBadge({ status }: { status: ItemStatus }) {
  const { t } = useSurface();
  return <Badge tone={statusTone(status)} testId="status-badge">{t(`portal.partners.content.status.${status}`)}</Badge>;
}

function FilterSelect({ label, name, value, options, any, onChange }: {
  label: string; name: string; value: string; options: { value: string; label: string }[]; any?: string; onChange: (v: string) => void;
}) {
  return (
    <Field label={label}>
      {(id) => (
        <select id={id} name={name} value={value} onChange={(e) => onChange(e.target.value)}>
          {any !== undefined ? <option value="">{any}</option> : null}
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
    </Field>
  );
}

/** Free-text search: commits to the URL 300 ms after typing stops, so the list does not refetch per keystroke. */
function SearchField({ value, onCommit, label }: { value: string; onCommit: (v: string) => void; label: string }) {
  const [text, setText] = useState(value);
  const commit = useRef(onCommit);
  commit.current = onCommit;
  useEffect(() => { setText(value); }, [value]);
  useEffect(() => {
    if (text.trim() === value) return;
    const id = setTimeout(() => commit.current(text.trim()), 300);
    return () => clearTimeout(id);
  }, [text, value]);
  return (
    <Field label={label}>
      {(id) => <input id={id} type="search" name="q" maxLength={100} value={text} onChange={(e) => setText(e.target.value)} />}
    </Field>
  );
}

/**
 * The list's filter bar (URL-synced). The calendar reuses it; `hide` drops a control the calendar sets itself (its month).
 */
export function FilterBar({ base, filters, onChange, onClear, hide = [] }: {
  base: Base; filters: Filters; onChange: (key: string, value: string) => void; onClear: () => void; hide?: readonly string[];
}) {
  const { t } = useSurface();
  const clients = base.clients.filter((c) => c.active);
  const ams = base.members.filter((m) => m.active && m.role === 'account_manager');
  const assignees = base.members.filter((m) => m.active);
  const types = base.catalog.contentTypes.filter((x) => x.active);
  const platforms = base.catalog.platforms.filter((x) => x.active);
  return (
    <form className="qx-toolbar qc-filters" role="search" aria-label={t('portal.partners.content.filter.region')} onSubmit={(e) => e.preventDefault()}>
      <FilterSelect label={t('portal.partners.content.filter.client')} name="client" value={filters.client ?? ''} any={t('portal.partners.content.filter.any')}
        options={clients.map((c) => ({ value: c.id, label: c.name }))} onChange={(v) => onChange('client', v)} />
      <FilterSelect label={t('portal.partners.content.filter.am')} name="am" value={filters.am ?? ''} any={t('portal.partners.content.filter.any')}
        options={ams.map((m) => ({ value: m.id, label: m.name }))} onChange={(v) => onChange('am', v)} />
      <FilterSelect label={t('portal.partners.content.filter.assignee')} name="assignee" value={filters.assignee ?? ''} any={t('portal.partners.content.filter.any')}
        options={assignees.map((m) => ({ value: m.id, label: m.name }))} onChange={(v) => onChange('assignee', v)} />
      <FilterSelect label={t('portal.partners.content.filter.type')} name="type" value={filters.type ?? ''} any={t('portal.partners.content.filter.any')}
        options={types.map((x) => ({ value: x.key, label: x.label }))} onChange={(v) => onChange('type', v)} />
      <FilterSelect label={t('portal.partners.content.filter.platform')} name="platform" value={filters.platform ?? ''} any={t('portal.partners.content.filter.any')}
        options={platforms.map((x) => ({ value: x.key, label: x.label }))} onChange={(v) => onChange('platform', v)} />
      <FilterSelect label={t('portal.partners.content.filter.status')} name="status" value={filters.status ?? ''} any={t('portal.partners.content.filter.any')}
        options={ITEM_STATUSES.map((s) => ({ value: s, label: t(`portal.partners.content.status.${s}`) }))} onChange={(v) => onChange('status', v)} />
      <FilterSelect label={t('portal.partners.content.filter.backlog')} name="backlog" value={filters.backlog ?? ''} any={t('portal.partners.content.filter.any')}
        options={[{ value: 'true', label: t('portal.partners.content.filter.yes') }, { value: 'false', label: t('portal.partners.content.filter.no') }]}
        onChange={(v) => onChange('backlog', v)} />
      <FilterSelect label={t('portal.partners.content.filter.kind')} name="kind" value={filters.kind ?? ''} any={t('portal.partners.content.filter.any')}
        options={[{ value: 'posting', label: t('portal.partners.content.kind.posting') }, { value: 'delivery', label: t('portal.partners.content.kind.delivery') }]}
        onChange={(v) => onChange('kind', v)} />
      {hide.includes('month') ? null : (
        <Field label={t('portal.partners.content.filter.month')}>
          {(id) => <input id={id} type="month" name="month" value={filters.month ?? ''} onChange={(e) => onChange('month', e.target.value)} />}
        </Field>
      )}
      <SearchField label={t('portal.partners.content.filter.search')} value={filters.q ?? ''} onCommit={(v) => onChange('q', v)} />
      <Button type="button" onClick={onClear} disabled={Object.keys(filters).length === 0}>{t('portal.partners.content.filter.clear')}</Button>
    </form>
  );
}

// ---------------------------------------------------------------- deliverables list

export function ContentListPage() {
  return <WithBase>{(b) => <ListBody base={b} />}</WithBase>;
}

function ListBody({ base }: { base: Base }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const filters = filtersFromParams(sp);
  const list = useResource(listKey(filters), () => api.deliverables(deliverableQuery(filters)));
  const saved = useResource<SavedFilter[]>('content:filters', () => api.filters());
  const [creating, setCreating] = useState(false);
  usePoll(list.reload, 5000);

  const update = (key: string, value: string) => setSp(setParam(sp, key, value));
  const clearAll = () => setSp(new URLSearchParams());
  const extra = FILTER_KEYS.filter((k) => !(BAR_KEYS as readonly string[]).includes(k) && filters[k]);

  return (
    <Screen title={t('portal.partners.content.list.title')} subtitle={t('portal.partners.content.list.subtitle')} eyebrow={t('portal.partners.content.eyebrow')}
      testId="content-list"
      actions={(
        <>
          <a className="qm-button" data-variant="secondary" href={exportHref('deliverables', filters)} download>{t('portal.partners.content.export.csv')}</a>
          <a className="qm-button" data-variant="secondary" href={exportHref('deliverables', filters, 'html', locale)} target="_blank" rel="noopener">{t('portal.partners.content.export.pdf')}</a>
          {base.me.can.editDeliverables ? <Button variant="primary" onClick={() => setCreating((v) => !v)} aria-expanded={creating}>{t('portal.partners.content.new.button')}</Button> : null}
        </>
      )}>
      <ContentNav me={base.me} />
      {creating ? (
        <NewDeliverableForm base={base} defaults={filters} onCancel={() => setCreating(false)} onCreated={(id) => nav(`/content/items/${encodeURIComponent(id)}`)} />
      ) : null}
      <FilterBar base={base} filters={filters} onChange={update} onClear={clearAll} />
      {extra.length > 0 ? (
        <Notice testId="content-extra-filters">
          {t('portal.partners.content.filter.extra', { list: extra.map((k) => `${t(`portal.partners.content.filter.${k}`)}: ${k === 'platformStatus' ? t(`portal.partners.content.platformStatus.${filters[k]}`) : filters[k]}`).join(', ') })}{' '}
          <Button type="button" onClick={() => { const n = new URLSearchParams(sp); for (const k of extra) n.delete(k); setSp(n); }}>{t('portal.partners.content.filter.clearExtra')}</Button>
        </Notice>
      ) : null}
      <SavedViews saved={saved} filters={filters} onLoad={(q) => setSp(new URLSearchParams(q))} />
      <Text tone="muted" data-testid="content-live">{t('portal.partners.content.list.live')}</Text>
      <Loadable resource={list}>
        {(env: PagedList<DeliverableView[]>) => (
          <>
            <Text tone="muted" data-testid="content-count">{t(env.meta.total === 1 ? 'portal.partners.content.list.count.one' : 'portal.partners.content.list.count.other', { n: env.meta.total })}</Text>
            {env.data.length < env.meta.total ? (
              <Notice testId="content-truncated">{t('portal.partners.content.list.truncated', { shown: env.data.length, total: env.meta.total })}</Notice>
            ) : null}
            <ItemTable rows={env.data} base={base} onChanged={list.reload} variant="full" />
          </>
        )}
      </Loadable>
    </Screen>
  );
}

function SavedViews({ saved, filters, onLoad }: { saved: Resource<SavedFilter[]>; filters: Filters; onLoad: (q: Record<string, string>) => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [pick, setPick] = useState('');
  const [name, setName] = useState('');
  const save = useAction((n: string) => api.saveFilter({ name: n, query: filters }));
  const del = useAction((id: string) => api.deleteFilter(id));
  const rows = saved.status === 'ready' ? saved.data : [];
  const doSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    if (await save.run(name.trim())) { setName(''); saved.reload(); }
  };
  const doDelete = async () => {
    if (!pick) return;
    if (await del.run(pick)) { setPick(''); saved.reload(); }
  };
  return (
    <section className="qc-saved" aria-label={t('portal.partners.content.saved.heading')} data-testid="content-saved">
      <span className="qx-field__label">{t('portal.partners.content.saved.heading')}</span>
      <div className="qx-toolbar">
        <Field label={t('portal.partners.content.saved.pick')}>
          {(id) => (
            <select id={id} value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">{rows.length ? t('portal.partners.content.saved.choose') : t('portal.partners.content.saved.none')}</option>
              {rows.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
        </Field>
        <Button type="button" disabled={!pick} onClick={() => { const s = rows.find((x) => x.id === pick); if (s) onLoad(s.query); }}>{t('portal.partners.content.saved.load')}</Button>
        <Button type="button" disabled={!pick || del.pending} onClick={() => void doDelete()}>{t('portal.partners.content.saved.delete')}</Button>
      </div>
      <form className="qx-toolbar" onSubmit={doSave}>
        <Field label={t('portal.partners.content.saved.name')}>
          {(id) => <input id={id} name="savedName" maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />}
        </Field>
        <Button type="submit" disabled={!name.trim() || save.pending || Object.keys(filters).length === 0}>{t('portal.partners.content.saved.save')}</Button>
      </form>
      <ContentError error={saved.status === 'error' ? saved.error : save.error ?? del.error} />
    </section>
  );
}

/** Shared row table for the list, My Work and the item lists. Stage and platform edits post, then refresh the rows. */
function ItemTable({ rows, base, onChanged, variant }: { rows: readonly DeliverableView[]; base: Base; onChanged: () => void; variant: 'full' | 'compact' }) {
  const { t, locale } = useSurface();
  const cols: Column<DeliverableView>[] = [
    {
      key: 'title', header: t('portal.partners.content.col.deliverable'),
      render: (d) => (
        <span className="qc-stack">
          <Link to={`/content/items/${encodeURIComponent(d.id)}`}>{d.title}</Link>
          <small>{d.clientName}</small>
        </span>
      ),
    },
    ...(variant === 'full' ? [{ key: 'type', header: t('portal.partners.content.col.type'), narrow: 'hide' as const, render: (d: DeliverableView) => catalogLabel(base.catalog.contentTypes, d.contentType) }] : []),
    {
      key: 'date', header: t('portal.partners.content.col.date'),
      render: (d) => (d.workingDate ? <time dateTime={d.workingDate}>{fmtDate(locale, d.workingDate)}</time> : <span>{t('portal.partners.content.noDate')}</span>),
    },
    { key: 'stage', header: t('portal.partners.content.col.stage'), render: (d) => <StageSelect item={d} stages={base.catalog.stages} canEdit={base.me.can.stage} onDone={onChanged} /> },
    ...(variant === 'full' ? [{ key: 'assignee', header: t('portal.partners.content.col.assignee'), narrow: 'hide' as const, render: (d: DeliverableView) => d.assigneeName ?? t('portal.partners.content.none') }] : []),
    {
      key: 'status', header: t('portal.partners.content.col.status'),
      render: (d) => (
        <span className="qc-stack">
          <StatusBadge status={d.status} />
          {d.backlog ? <Badge tone="warn">{t('portal.partners.content.backlogFlag')}</Badge> : null}
        </span>
      ),
    },
    {
      key: 'work', header: t('portal.partners.content.col.work'),
      render: (d) => (d.clientKind === 'posting'
        ? (
          <span className="qc-stack">
            <PlatformQuick item={d} platforms={base.catalog.platforms} canEdit={base.me.can.platformStatus} onDone={onChanged} />
            {base.me.can.platformStatus ? <MarkPosted item={d} platforms={base.catalog.platforms} onDone={onChanged} /> : null}
          </span>
        )
        : <Text>{d.approval ? t(`portal.partners.content.approval.${d.approval}`) : t('portal.partners.content.approval.none')}</Text>),
    },
  ];
  return <DataTable columns={cols} rows={rows} rowKey={(d) => d.id} empty={t('portal.partners.content.list.empty')} caption={t('portal.partners.content.list.caption')} />;
}

/** Stage select on the row: one change saves (NFR-17). The version guards against overwriting (NFR-16). */
function StageSelect({ item, stages, canEdit, onDone }: { item: DeliverableView; stages: readonly CatalogRow[]; canEdit: boolean; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const act = useAction((stage: string) => api.stage(item.id, stage || null, item.version));
  const options = stages.filter((s) => s.active || s.key === item.stage);
  return (
    <span className="qc-inline">
      <select aria-label={t('portal.partners.content.stage.labelFor', { title: item.title })} name="stage" value={item.stage ?? ''}
        disabled={!canEdit || act.pending}
        onChange={async (e) => { if (await act.run(e.target.value)) onDone(); }}>
        <option value="">{t('portal.partners.content.stage.none')}</option>
        {options.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
      </select>
      <ContentError error={act.error} />
    </span>
  );
}

/** One button per planned platform: one click marks it Posted, a second click returns it to Planned. */
function PlatformQuick({ item, platforms, canEdit, onDone }: { item: DeliverableView; platforms: readonly CatalogRow[]; canEdit: boolean; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const act = useAction((platform: string, status: string) => api.setPlatform(item.id, platform, { status }));
  if (item.platforms.length === 0) return <Text tone="muted">{t('portal.partners.content.platform.none')}</Text>;
  return (
    <span className="qc-platforms" role="group" aria-label={t('portal.partners.content.platform.group')}>
      {item.platforms.map((p) => {
        const posted = p.status === 'posted';
        return (
          <button key={p.platform} type="button" className="qc-platform" aria-pressed={posted} disabled={!canEdit || act.pending} data-status={p.status}
            onClick={async () => { if (await act.run(p.platform, posted ? 'planned' : 'posted')) onDone(); }}>
            <span>{catalogLabel(platforms, p.platform)}</span>
            <span className="qc-platform__status">{t(`portal.partners.content.platformStatus.${p.status}`)}</span>
          </button>
        );
      })}
      <ContentError error={act.error} />
    </span>
  );
}

/**
 * US-13 "Mark posted": tick the platforms that went live, then confirm once. One request marks them all posted.
 * Shown only where the caller may change platform status (the caller checks `can.platformStatus`).
 */
function MarkPosted({ item, platforms, onDone }: { item: DeliverableView; platforms: readonly CatalogRow[]; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const candidates = postablePlatforms(item.platforms);
  const act = useAction((keys: string[]) => api.bulkPlatforms(item.id, { platforms: keys, status: 'posted' }));
  if (candidates.length === 0) return null;
  const close = () => { setOpen(false); setPicked([]); };
  const confirm = async () => {
    if (picked.length === 0) return;
    if (await act.run(picked)) { close(); onDone(); }
  };
  return (
    <span className="qc-mark">
      <Button type="button" aria-expanded={open} aria-controls={panelId} disabled={act.pending} onClick={() => (open ? close() : setOpen(true))}>
        {t('portal.partners.content.markPosted.button')}
      </Button>
      {open ? (
        <fieldset className="qc-fieldset qc-mark__panel" id={panelId}>
          <legend>{t('portal.partners.content.markPosted.legend')}</legend>
          <div className="qc-checks">
            {candidates.map((key) => (
              <Checkbox key={key} name="markPosted" label={catalogLabel(platforms, key)} checked={picked.includes(key)} disabled={act.pending}
                onChange={(on) => setPicked((x) => (on ? [...new Set([...x, key])] : x.filter((k) => k !== key)))} />
            ))}
          </div>
          <span className="qc-inline">
            <Button type="button" variant="primary" disabled={picked.length === 0 || act.pending} onClick={() => void confirm()}>
              {t('portal.partners.content.markPosted.confirm', { n: picked.length })}
            </Button>
            <Button type="button" onClick={close} disabled={act.pending}>{t('portal.partners.content.markPosted.cancel')}</Button>
          </span>
          {picked.length === 0 ? <Text tone="muted">{t('portal.partners.content.markPosted.pick')}</Text> : null}
          <ContentError error={act.error} />
        </fieldset>
      ) : null}
    </span>
  );
}

function NewDeliverableForm({ base, defaults, onCreated, onCancel }: { base: Base; defaults: Filters; onCreated: (id: string) => void; onCancel: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const clients = base.clients.filter((c) => c.active);
  const types = base.catalog.contentTypes.filter((x) => x.active);
  const platformRows = base.catalog.platforms.filter((x) => x.active);
  const stages = base.catalog.stages.filter((s) => s.active);
  const assignees = base.members.filter((m) => m.active);
  const [clientId, setClientId] = useState(defaults.client && clients.some((c) => c.id === defaults.client) ? defaults.client : clients[0]?.id ?? '');
  const client = clients.find((c) => c.id === clientId);
  const [contentType, setContentType] = useState(types[0]?.key ?? '');
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(defaults.from ?? base.me.today);
  const [assigneeId, setAssigneeId] = useState('');
  const [stage, setStage] = useState('');
  // The API refuses inactive platform keys, so a client's platforms are kept only while they are still active.
  const activePlatformKeys = new Set(platformRows.map((p) => p.key));
  const liveOf = (keys: readonly string[]) => keys.filter((k) => activePlatformKeys.has(k));
  const [platforms, setPlatforms] = useState<string[]>(liveOf(client?.platforms ?? []));
  const [deliveredOn, setDeliveredOn] = useState('');
  const [fileLink, setFileLink] = useState('');
  const [tried, setTried] = useState(false);
  const act = useAction(async (body: Record<string, unknown>, delivery: { deliveredOn?: string; fileLink?: string } | null) => {
    const created = await api.createDeliverable(body);
    if (delivery) await api.delivery(created.id, delivery);
    return created;
  });

  const pickClient = (id: string) => {
    setClientId(id);
    setPlatforms(liveOf(clients.find((c) => c.id === id)?.platforms ?? []));
  };
  const errs = {
    client: !client,
    type: !contentType,
    title: !title.trim(),
    date: !date,
    platforms: client?.kind === 'posting' && platforms.length === 0,
    fileLink: !!fileLink && !isHttps(fileLink),
  };
  const invalid = Object.values(errs).some(Boolean);
  const togglePlatform = (key: string, on: boolean) => setPlatforms((p) => (on ? [...new Set([...p, key])] : p.filter((x) => x !== key)));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (invalid || !client) return;
    const posting = client.kind === 'posting';
    const body: Record<string, unknown> = {
      clientId: client.id, contentType, title: title.trim(), originalDate: date,
      ...(assigneeId ? { assigneeId } : {}),
      ...(stage ? { stage } : {}),
      ...(posting ? { platforms } : {}),
    };
    const delivery = !posting && (deliveredOn || fileLink) ? { ...(deliveredOn ? { deliveredOn } : {}), ...(fileLink ? { fileLink } : {}) } : null;
    const created = await act.run(body, delivery);
    if (created) onCreated(created.id);
  };

  return (
    <section className="qc-panel" aria-labelledby="content-new-heading" data-testid="content-new">
      <Heading level={2} id="content-new-heading">{t('portal.partners.content.new.title')}</Heading>
      <form onSubmit={submit} noValidate>
        <div className="qc-form-grid">
          <Field label={t('portal.partners.content.new.client')} error={tried && errs.client ? t('portal.partners.content.new.required') : null}>
            {(id, d) => (
              <select id={id} name="clientId" required value={clientId} aria-describedby={d} onChange={(e) => pickClient(e.target.value)}>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            )}
          </Field>
          <Field label={t('portal.partners.content.new.type')} error={tried && errs.type ? t('portal.partners.content.new.required') : null}>
            {(id, d) => (
              <select id={id} name="contentType" required value={contentType} aria-describedby={d} onChange={(e) => setContentType(e.target.value)}>
                {types.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            )}
          </Field>
          <Field label={t('portal.partners.content.new.title')} error={tried && errs.title ? t('portal.partners.content.new.required') : null}>
            {(id, d) => <input id={id} name="title" required maxLength={200} value={title} aria-describedby={d} onChange={(e) => setTitle(e.target.value)} />}
          </Field>
          <Field label={t('portal.partners.content.new.date')} error={tried && errs.date ? t('portal.partners.content.new.required') : null}>
            {(id, d) => <input id={id} type="date" name="originalDate" required value={date} aria-describedby={d} onChange={(e) => setDate(e.target.value)} />}
          </Field>
          <Field label={t('portal.partners.content.new.assignee')}>
            {(id) => (
              <select id={id} name="assigneeId" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
                <option value="">{t('portal.partners.content.none')}</option>
                {assignees.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            )}
          </Field>
          <Field label={t('portal.partners.content.new.stage')}>
            {(id) => (
              <select id={id} name="stage" value={stage} onChange={(e) => setStage(e.target.value)}>
                <option value="">{t('portal.partners.content.stage.none')}</option>
                {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            )}
          </Field>
        </div>
        {client?.kind === 'posting' ? (
          <fieldset className="qc-fieldset">
            <legend>{t('portal.partners.content.new.platforms')}</legend>
            <Text tone="muted">{t('portal.partners.content.new.platformsHint')}</Text>
            <div className="qc-checks">
              {platformRows.map((p) => (
                <Checkbox key={p.key} name="platforms" label={p.label} checked={platforms.includes(p.key)} onChange={(on) => togglePlatform(p.key, on)} />
              ))}
            </div>
            {tried && errs.platforms ? <Text role="alert">{t('portal.partners.content.new.platformsRequired')}</Text> : null}
          </fieldset>
        ) : null}
        {client?.kind === 'delivery' ? (
          <fieldset className="qc-fieldset">
            <legend>{t('portal.partners.content.new.delivery')}</legend>
            <div className="qc-form-grid">
              <Field label={t('portal.partners.content.new.deliveredOn')}>
                {(id) => <input id={id} type="date" name="deliveredOn" value={deliveredOn} onChange={(e) => setDeliveredOn(e.target.value)} />}
              </Field>
              <Field label={t('portal.partners.content.new.fileLink')} hint={t('portal.partners.content.httpsHint')} error={tried && errs.fileLink ? t('portal.partners.content.httpsOnly') : null}>
                {(id, d) => <input id={id} type="url" name="fileLink" inputMode="url" value={fileLink} aria-describedby={d} onChange={(e) => setFileLink(e.target.value)} />}
              </Field>
            </div>
          </fieldset>
        ) : null}
        <div className="qx-screen__actions">
          <Button type="submit" variant="primary" disabled={act.pending}>{t('portal.partners.content.new.submit')}</Button>
          <Button type="button" onClick={onCancel}>{t('portal.partners.content.cancel')}</Button>
        </div>
      </form>
      <ContentError error={act.error} />
    </section>
  );
}

// ---------------------------------------------------------------- item

export function ContentItemPage() {
  const { id = '' } = useParams();
  return <WithBase>{(b) => <ItemBody base={b} id={id} />}</WithBase>;
}

function ItemBody({ base, id }: { base: Base; id: string }) {
  const api = useApi();
  const detail = useResource(`content:item:${id}`, () => api.deliverable(id));
  return <Loadable resource={detail}>{(d) => <ItemView base={base} detail={d} reload={detail.reload} />}</Loadable>;
}

function ItemView({ base, detail, reload }: { base: Base; detail: DeliverableDetail; reload: () => void }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const nav = useNavigate();
  const d = detail.item;
  const can = base.me.can;
  const dup = useAction(() => api.duplicate(d.id));
  const del = useAction(() => api.deleteDeliverable(d.id));
  const remove = async () => {
    if (!window.confirm(t('portal.partners.content.item.deleteConfirm'))) return;
    if (await del.run()) nav('/content');
  };
  const copy = async () => {
    const created = await dup.run();
    if (created) nav(`/content/items/${encodeURIComponent(created.id)}`);
  };
  const stageLabel = d.stage ? catalogLabel(base.catalog.stages, d.stage) : t('portal.partners.content.none');
  const assignee = base.members.find((m) => m.id === d.assigneeId);

  return (
    <Screen title={d.title} subtitle={`${d.clientName} · ${catalogLabel(base.catalog.contentTypes, d.contentType)}`} eyebrow={t('portal.partners.content.eyebrow')}
      testId="content-item" actions={<StatusBadge status={d.status} />}>
      <ContentNav me={base.me} />
      <p><Link to="/content">{t('portal.partners.content.item.back')}</Link></p>
      {can.editDeliverables ? (
        <div className="qx-screen__actions">
          <Button type="button" onClick={() => void copy()} disabled={dup.pending}>{t('portal.partners.content.item.duplicate')}</Button>
          <Button type="button" onClick={() => void remove()} disabled={del.pending}>{t('portal.partners.content.item.delete')}</Button>
        </div>
      ) : null}
      <ContentError error={dup.error ?? del.error} />

      <section className="qc-panel" aria-labelledby="content-details">
        <Heading level={2} id="content-details">{t('portal.partners.content.item.details')}</Heading>
        <dl className="qc-kv">
          <dt>{t('portal.partners.content.col.client')}</dt><dd>{d.clientName} · {t(`portal.partners.content.kind.${d.clientKind}`)}</dd>
          <dt>{t('portal.partners.content.item.originalDate')}</dt>
          <dd>{d.originalDate ? <time dateTime={d.originalDate}>{fmtDate(locale, d.originalDate)}</time> : t('portal.partners.content.noDate')}
            <small className="qc-hint">{t('portal.partners.content.item.lockedHint')}</small></dd>
          <dt>{t('portal.partners.content.item.rescheduledDate')}</dt><dd>{d.rescheduledDate ? fmtDate(locale, d.rescheduledDate) : t('portal.partners.content.none')}</dd>
          <dt>{t('portal.partners.content.col.date')}</dt><dd>{d.workingDate ? fmtDate(locale, d.workingDate) : t('portal.partners.content.noDate')}</dd>
          <dt>{t('portal.partners.content.col.stage')}</dt><dd>{stageLabel}</dd>
          <dt>{t('portal.partners.content.col.assignee')}</dt><dd>{assignee?.name ?? d.assigneeName ?? t('portal.partners.content.none')}</dd>
          {d.clientKind === 'posting' ? (
            <>
              <dt>{t('portal.partners.content.item.actualPostDate')}</dt><dd>{d.actualPostDate ? fmtDate(locale, d.actualPostDate) : t('portal.partners.content.none')}</dd>
            </>
          ) : (
            <>
              <dt>{t('portal.partners.content.item.deliveredOn')}</dt><dd>{d.deliveredOn ? fmtDate(locale, d.deliveredOn) : t('portal.partners.content.none')}</dd>
              <dt>{t('portal.partners.content.item.approval')}</dt><dd>{d.approval ? t(`portal.partners.content.approval.${d.approval}`) : t('portal.partners.content.approval.none')}</dd>
            </>
          )}
          <dt>{t('portal.partners.content.item.links')}</dt>
          <dd>{d.links.length === 0 ? t('portal.partners.content.none') : (
            <ul className="qc-list">{d.links.map((l) => <li key={l.url}><a href={l.url} target="_blank" rel="noopener noreferrer">{l.label || l.url}</a></li>)}</ul>
          )}</dd>
          <dt>{t('portal.partners.content.item.remarks')}</dt><dd>{d.remarks || t('portal.partners.content.none')}</dd>
        </dl>
      </section>

      {can.editDeliverables ? <EditForm key={d.version} base={base} d={d} onSaved={reload} /> : null}
      {can.editDeliverables ? <RescheduleForm key={`r${d.version}`} d={d} onDone={reload} /> : null}
      {can.originalDate && d.originalDate ? <OriginalDateForm d={d} onDone={reload} /> : null}
      {can.stage ? (
        <section className="qc-panel" aria-labelledby="content-stage">
          <Heading level={2} id="content-stage">{t('portal.partners.content.item.stage')}</Heading>
          <StageSelect item={d} stages={base.catalog.stages} canEdit={can.stage} onDone={reload} />
        </section>
      ) : null}
      {d.clientKind === 'posting' && can.platformStatus ? (
        <section className="qc-panel" aria-labelledby="content-platforms">
          <Heading level={2} id="content-platforms">{t('portal.partners.content.item.platforms')}</Heading>
          {d.platforms.length === 0 ? <Text tone="muted">{t('portal.partners.content.platform.none')}</Text> : null}
          <MarkPosted item={d} platforms={base.catalog.platforms} onDone={reload} />
          {d.platforms.map((p) => {
            const label = catalogLabel(base.catalog.platforms, p.platform);
            const sync = p as typeof p & PlatformSyncFields;
            return (
              <Fragment key={`${p.platform}:${p.status}:${p.postUrl ?? ''}`}>
                <PlatformEditRow itemId={d.id} platform={p.platform} label={label} status={p.status} postUrl={p.postUrl} onDone={reload} />
                <PlatformSyncNote platform={label} status={p.status} syncedAt={sync.syncedAt} syncError={sync.syncError} />
              </Fragment>
            );
          })}
        </section>
      ) : null}
      {d.clientKind === 'delivery' && can.delivery ? <DeliveryForm d={d} today={base.me.today} onDone={reload} /> : null}
      {d.clientKind === 'delivery' && can.delivery ? <ApprovalForm d={d} onDone={reload} /> : null}
      {d.clientKind === 'delivery' && can.editDeliverables ? <ShareForApproval d={d} /> : null}

      <section className="qc-panel" aria-labelledby="content-files">
        <Heading level={2} id="content-files">{t('portal.partners.content.item.files')}</Heading>
        {detail.files.length === 0 ? <Text tone="muted">{t('portal.partners.content.item.noFiles')}</Text> : (
          <ul className="qc-list">
            {detail.files.map((f) => (
              <li key={f.id}>
                <strong>{t('portal.partners.content.item.version', { n: f.versionNo })}</strong>{' '}
                <a href={fileHref(f.url)} target="_blank" rel="noopener noreferrer">{f.label || f.url}</a>{' '}
                {f.assetId ? <><Badge>{t('portal.partners.content.library.fromLibrary')}</Badge>{' '}</> : null}
                <small className="qc-hint">{fmtDate(locale, f.createdAt.slice(0, 10))}</small>
              </li>
            ))}
          </ul>
        )}
        {can.stage || can.editDeliverables ? <FileForm itemId={d.id} onDone={reload} /> : null}
        {can.stage || can.editDeliverables ? <LibraryPicker itemId={d.id} onDone={reload} /> : null}
      </section>

      <section className="qc-panel" aria-labelledby="content-activity">
        <Heading level={2} id="content-activity">{t('portal.partners.content.item.activity')}</Heading>
        <ActivityTable rows={detail.activity} members={base.members} />
      </section>
    </Screen>
  );
}

function EditForm({ base, d, onSaved }: { base: Base; d: DeliverableView; onSaved: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const types = base.catalog.contentTypes.filter((x) => x.active || x.key === d.contentType);
  const assignees = base.members.filter((m) => m.active || m.id === d.assigneeId);
  const platforms = base.catalog.platforms.filter((x) => x.active || d.platforms.some((p) => p.platform === x.key));
  const [title, setTitle] = useState(d.title);
  const [contentType, setContentType] = useState(d.contentType);
  const [assigneeId, setAssigneeId] = useState(d.assigneeId ?? '');
  const [remarks, setRemarks] = useState(d.remarks ?? '');
  const [actual, setActual] = useState(d.actualPostDate ?? '');
  const [originalDate, setOriginalDate] = useState('');
  const currentPlatforms = d.platforms.map((p) => p.platform);
  // Posted and missed platforms are history: the API will not remove them, so the form keeps them checked and locked.
  const locked = new Set(d.platforms.filter((p) => p.status === 'posted' || p.status === 'missed').map((p) => p.platform));
  const [picked, setPicked] = useState<string[]>(currentPlatforms);
  const act = useAction((body: Record<string, unknown>) => api.updateDeliverable(d.id, body as Record<string, unknown> & { version: number }));
  const posting = d.clientKind === 'posting';
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const body: Record<string, unknown> = {
      version: d.version, title: title.trim(), contentType, assigneeId: assigneeId || null, remarks: remarks.trim() || null,
      ...(posting ? { actualPostDate: actual || null } : {}),
      ...(posting && platformsChanged(currentPlatforms, picked) ? { platforms: picked } : {}),
      ...(d.originalDate === null && originalDate ? { originalDate } : {}),
    };
    if (await act.run(body)) onSaved();
  };
  return (
    <section className="qc-panel" aria-labelledby="content-edit">
      <Heading level={2} id="content-edit">{t('portal.partners.content.item.edit')}</Heading>
      <form onSubmit={submit} noValidate>
        <div className="qc-form-grid">
          <Field label={t('portal.partners.content.new.title')} error={!title.trim() ? t('portal.partners.content.new.required') : null}>
            {(id, desc) => <input id={id} name="title" required maxLength={200} value={title} aria-describedby={desc} onChange={(e) => setTitle(e.target.value)} />}
          </Field>
          <Field label={t('portal.partners.content.col.type')}>
            {(id) => <select id={id} name="contentType" value={contentType} onChange={(e) => setContentType(e.target.value)}>
              {types.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
            </select>}
          </Field>
          <Field label={t('portal.partners.content.col.assignee')}>
            {(id) => <select id={id} name="assigneeId" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
              <option value="">{t('portal.partners.content.none')}</option>
              {assignees.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>}
          </Field>
          {posting ? (
            <Field label={t('portal.partners.content.item.actualPostDate')} hint={t('portal.partners.content.item.actualPostHint')}>
              {(id) => <input id={id} type="date" name="actualPostDate" value={actual} onChange={(e) => setActual(e.target.value)} />}
            </Field>
          ) : null}
          {d.originalDate === null ? (
            <Field label={t('portal.partners.content.item.originalDate')} hint={t('portal.partners.content.item.undatedHint')}>
              {(id) => <input id={id} type="date" name="originalDate" value={originalDate} onChange={(e) => setOriginalDate(e.target.value)} />}
            </Field>
          ) : null}
        </div>
        <Field label={t('portal.partners.content.item.remarks')}>
          {(id) => <textarea id={id} name="remarks" maxLength={2000} value={remarks} onChange={(e) => setRemarks(e.target.value)} />}
        </Field>
        {posting ? (
          <fieldset className="qc-fieldset">
            <legend>{t('portal.partners.content.new.platforms')}</legend>
            <div className="qc-checks">
              {platforms.map((p) => (
                <Checkbox key={p.key} name="platforms" label={p.label} checked={picked.includes(p.key)} disabled={locked.has(p.key)}
                  onChange={(on) => { if (!locked.has(p.key)) setPicked((x) => (on ? [...new Set([...x, p.key])] : x.filter((k) => k !== p.key))); }} />
              ))}
            </div>
            {locked.size > 0 ? <Text tone="muted">{t('portal.partners.content.item.platformLocked')}</Text> : null}
          </fieldset>
        ) : null}
        <Button type="submit" variant="primary" disabled={act.pending || !title.trim()}>{t('portal.partners.content.save')}</Button>
      </form>
      <ContentError error={act.error} />
      {act.result ? <Notice testId="content-saved-notice">{t('portal.partners.content.item.saved')}</Notice> : null}
    </section>
  );
}

function RescheduleForm({ d, onDone }: { d: DeliverableView; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [date, setDate] = useState(d.rescheduledDate ?? '');
  const [reason, setReason] = useState('');
  const act = useAction((body: { date: string | null; reason?: string }) => api.reschedule(d.id, body));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await act.run({ date: date || null, ...(reason.trim() ? { reason: reason.trim() } : {}) })) onDone();
  };
  return (
    <section className="qc-panel" aria-labelledby="content-reschedule">
      <Heading level={2} id="content-reschedule">{t('portal.partners.content.item.reschedule')}</Heading>
      <form onSubmit={submit} className="qc-form-grid">
        <Field label={t('portal.partners.content.item.rescheduledDate')} hint={t('portal.partners.content.item.rescheduleHint')}>
          {(id) => <input id={id} type="date" name="date" value={date} onChange={(e) => setDate(e.target.value)} />}
        </Field>
        <Field label={t('portal.partners.content.item.reason')}>
          {(id) => <input id={id} name="reason" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <div><Button type="submit" disabled={act.pending}>{t('portal.partners.content.item.rescheduleSubmit')}</Button></div>
      </form>
      <ContentError error={act.error} />
    </section>
  );
}

/** BR-01: the original date is locked after creation; only an admin may change it, with a logged reason. */
function OriginalDateForm({ d, onDone }: { d: DeliverableView; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [date, setDate] = useState(d.originalDate ?? '');
  const [reason, setReason] = useState('');
  const act = useAction((body: { date: string; reason: string }) => api.originalDate(d.id, body));
  const ok = !!date && reason.trim().length >= 3;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (ok && await act.run({ date, reason: reason.trim() })) onDone();
  };
  return (
    <section className="qc-panel" aria-labelledby="content-original">
      <Heading level={2} id="content-original">{t('portal.partners.content.item.adminOriginal')}</Heading>
      <form onSubmit={submit} className="qc-form-grid">
        <Field label={t('portal.partners.content.item.originalDate')}>
          {(id) => <input id={id} type="date" name="originalDate" required value={date} onChange={(e) => setDate(e.target.value)} />}
        </Field>
        <Field label={t('portal.partners.content.item.reason')} hint={t('portal.partners.content.item.reasonHint')}>
          {(id) => <input id={id} name="reason" required minLength={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />}
        </Field>
        <div><Button type="submit" disabled={!ok || act.pending}>{t('portal.partners.content.item.originalSubmit')}</Button></div>
      </form>
      <ContentError error={act.error} />
    </section>
  );
}

/** US-15: what the platform sync did for this row: the time it marked the post Posted, or why it could not check it. */
function PlatformSyncNote({ platform, status, syncedAt, syncError }: { platform: string; status: string; syncedAt?: string | null; syncError?: string | null }) {
  const { t, locale } = useSurface();
  if (status === 'posted' && syncedAt) {
    return <Text tone="muted"><span data-testid="sync-note">{t('portal.partners.content.sync.syncedFrom', { platform, time: formatWhen(locale, syncedAt) })}</span></Text>;
  }
  if (syncError && (status === 'planned' || status === 'scheduled')) {
    return <div role="status" data-testid="sync-error" className="qx-alert">{t(`portal.partners.content.sync.error.${syncErrorKey(syncError)}`)}</div>;
  }
  return null;
}

/** US-15 client settings: which connected account this client's posts are synced from. Admin only (the API enforces it). */
function SyncAccountPicker({ client, onDone }: { client: ContentClient; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const accounts = useResource<SocialAccountOption[]>(`content:social-accounts:${client.id}`, () => api.clientSocialAccounts(client.id));
  const act = useAction((socialAccountId: string | null) => api.updateClient(client.id, { socialAccountId }));
  if (!client.tenantId) return <Text tone="muted">{t('portal.partners.content.sync.needsWorkspace')}</Text>;
  if (accounts.status === 'error') return <ContentError error={accounts.error} />;
  if (accounts.status !== 'ready') return <Text tone="muted">{t('portal.partners.content.sync.loading')}</Text>;
  const options = accounts.data ?? [];
  const linked = client.socialAccountId ?? '';
  const linkedMissing = linked !== '' && !options.some((a) => a.id === linked);
  return (
    <div>
      <Field label={t('portal.partners.content.sync.account')}>
        {(id) => (
          <select id={id} name={`sync-${client.id}`} value={linked} disabled={act.pending}
            onChange={async (e) => { if (await act.run(e.target.value || null)) onDone(); }}>
            <option value="">{t('portal.partners.content.sync.none')}</option>
            {linkedMissing ? <option value={linked}>{t('portal.partners.content.sync.unavailable')}</option> : null}
            {options.map((a) => <option key={a.id} value={a.id}>{`${a.displayName} (${PROVIDER_NAMES[a.provider]})`}</option>)}
          </select>
        )}
      </Field>
      <ContentError error={act.error} />
    </div>
  );
}

function PlatformEditRow({ itemId, platform, label, status, postUrl, onDone }: {
  itemId: string; platform: string; label: string; status: string; postUrl: string | null; onDone: () => void;
}) {
  const { t } = useSurface();
  const api = useApi();
  const [value, setValue] = useState(status);
  const [url, setUrl] = useState(postUrl ?? '');
  const act = useAction((body: { status: string; postUrl?: string | null }) => api.setPlatform(itemId, platform, body));
  const urlOk = !url.trim() || isHttps(url);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (urlOk && await act.run({ status: value, postUrl: url.trim() || null })) onDone();
  };
  return (
    <form className="qc-platform-row" onSubmit={save}>
      <span className="qc-platform-row__name">{label}</span>
      <Field label={t('portal.partners.content.item.platformStatus', { platform: label })}>
        {(id) => (
          <select id={id} name={`status-${platform}`} value={value} onChange={(e) => setValue(e.target.value)}>
            {PLATFORM_STATUSES.map((s) => <option key={s} value={s}>{t(`portal.partners.content.platformStatus.${s}`)}</option>)}
          </select>
        )}
      </Field>
      <Field label={t('portal.partners.content.item.postUrl')} hint={t('portal.partners.content.item.postUrlHint')} error={urlOk ? null : t('portal.partners.content.httpsOnly')}>
        {(id, desc) => <input id={id} type="url" name={`url-${platform}`} inputMode="url" value={url} aria-describedby={desc} onChange={(e) => setUrl(e.target.value)} />}
      </Field>
      {value === 'posted' && !url.trim() ? <Text tone="muted">{t('portal.partners.content.item.postUrlPrompt')}</Text> : null}
      <Button type="submit" disabled={act.pending || !urlOk}>{t('portal.partners.content.save')}</Button>
      <ContentError error={act.error} />
    </form>
  );
}

function DeliveryForm({ d, today, onDone }: { d: DeliverableView; today: string; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [deliveredOn, setDeliveredOn] = useState(d.deliveredOn ?? today);
  const [fileLink, setFileLink] = useState(d.deliveryLink ?? '');
  const act = useAction((body: { deliveredOn?: string; fileLink?: string | null }) => api.delivery(d.id, body));
  const unlink = useAction(() => api.delivery(d.id, { fileLink: null }));
  const linkOk = !fileLink.trim() || isHttps(fileLink);
  // an emptied link box sends null, which clears the saved link (the delivered date is kept by the server)
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (linkOk && await act.run({ ...(deliveredOn ? { deliveredOn } : {}), fileLink: fileLink.trim() || null })) onDone();
  };
  const removeLink = async () => {
    if (await unlink.run()) { setFileLink(''); onDone(); }
  };
  return (
    <section className="qc-panel" aria-labelledby="content-delivery">
      <Heading level={2} id="content-delivery">{t('portal.partners.content.item.delivery')}</Heading>
      <form onSubmit={submit} className="qc-form-grid">
        <Field label={t('portal.partners.content.new.deliveredOn')}>
          {(id) => <input id={id} type="date" name="deliveredOn" value={deliveredOn} onChange={(e) => setDeliveredOn(e.target.value)} />}
        </Field>
        <Field label={t('portal.partners.content.new.fileLink')} hint={t('portal.partners.content.httpsHint')} error={linkOk ? null : t('portal.partners.content.httpsOnly')}>
          {(id, desc) => <input id={id} type="url" name="fileLink" inputMode="url" value={fileLink} aria-describedby={desc} onChange={(e) => setFileLink(e.target.value)} />}
        </Field>
        <div><Button type="submit" disabled={act.pending || !linkOk || !deliveredOn}>{t('portal.partners.content.item.recordDelivery')}</Button></div>
      </form>
      {d.deliveryLink ? (
        <div className="qx-screen__actions" data-testid="content-remove-link">
          <Button type="button" onClick={() => void removeLink()} disabled={unlink.pending || act.pending}>{t('portal.partners.content.item.removeLink')}</Button>
          <Text tone="muted">{t('portal.partners.content.item.removeLinkHint')}</Text>
        </div>
      ) : null}
      <ContentError error={act.error ?? unlink.error} />
    </section>
  );
}

function ApprovalForm({ d, onDone }: { d: DeliverableView; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [approval, setApproval] = useState<string>(d.approval ?? 'pending');
  const [note, setNote] = useState('');
  const act = useAction((body: { approval: string; note?: string }) => api.approval(d.id, body));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await act.run({ approval, ...(note.trim() ? { note: note.trim() } : {}) })) { setNote(''); onDone(); }
  };
  return (
    <section className="qc-panel" aria-labelledby="content-approval">
      <Heading level={2} id="content-approval">{t('portal.partners.content.item.approvalHeading')}</Heading>
      <form onSubmit={submit} className="qc-form-grid">
        <Field label={t('portal.partners.content.item.approval')}>
          {(id) => (
            <select id={id} name="approval" value={approval} onChange={(e) => setApproval(e.target.value)}>
              {APPROVALS.map((a) => <option key={a} value={a}>{t(`portal.partners.content.approval.${a}`)}</option>)}
            </select>
          )}
        </Field>
        <Field label={t('portal.partners.content.item.note')}>
          {(id) => <input id={id} name="note" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />}
        </Field>
        <div><Button type="submit" disabled={act.pending}>{t('portal.partners.content.item.approvalSubmit')}</Button></div>
      </form>
      <ContentError error={act.error} />
    </section>
  );
}

/** At most this many items per approval link (the API's limit) and recipients per email. */
export const SHARE_MAX_ITEMS = 20;
export const SHARE_MAX_RECIPIENTS = 5;
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]{2,}$/;

/** Splits what the agency typed (commas, semicolons, spaces or new lines) into unique, lower-cased addresses. */
export function parseRecipients(input: string): { valid: string[]; invalid: string[] } {
  const parts = input.split(/[\s,;]+/).map((p) => p.trim().toLowerCase()).filter(Boolean);
  const unique = [...new Set(parts)];
  return { valid: unique.filter((p) => EMAIL_RE.test(p)), invalid: unique.filter((p) => !EMAIL_RE.test(p)) };
}

/** Other delivered items of the same client that can go on the same link (this item is always on it). */
export function shareCandidates(d: DeliverableView, rows: DeliverableView[]): DeliverableView[] {
  return rows.filter((r) => r.id !== d.id && r.clientId === d.clientId && r.deliveredOn);
}

/** US-18: a link the client opens without signing in to approve these items or ask for changes. The URL is shown once. */
function ShareForApproval({ d }: { d: DeliverableView }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const links = useResource(`content:approval-links:${d.id}`, () => api.approvalLinks(d.id));
  const [created, setCreated] = useState<ApprovalLinkCreated | null>(null);
  const [extra, setExtra] = useState<string[]>([]);
  const [byEmail, setByEmail] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [mailLocale, setMailLocale] = useState<'en' | 'ar'>(locale === 'ar' ? 'ar' : 'en');
  const [formError, setFormError] = useState<string | null>(null);
  const make = useAction((body: ApprovalLinkCreateBody) => api.createApprovalLink(body));
  const revoke = useAction((id: string) => api.revokeApprovalLink(id));
  const toggle = (id: string, on: boolean) => setExtra((cur) => (on ? (cur.includes(id) ? cur : [...cur, id]) : cur.filter((x) => x !== id)));
  const create = async () => {
    setFormError(null);
    const body: ApprovalLinkCreateBody = { deliverableIds: [d.id, ...extra] };
    if (byEmail) {
      const { valid, invalid } = parseRecipients(recipients);
      if (invalid.length) return setFormError(t('portal.partners.content.share.email.invalid', { list: invalid.join(', ') }));
      if (!valid.length) return setFormError(t('portal.partners.content.share.email.required'));
      if (valid.length > SHARE_MAX_RECIPIENTS) return setFormError(t('portal.partners.content.share.email.tooMany', { n: SHARE_MAX_RECIPIENTS }));
      body.email = { to: valid, locale: mailLocale };
    }
    const r = await make.run(body);
    if (r) { setCreated(r); setExtra([]); setRecipients(''); links.reload(); }
  };
  const cancel = async (id: string) => {
    if (!window.confirm(t('portal.partners.content.share.revokeConfirm'))) return;
    if (await revoke.run(id)) links.reload();
  };
  const tone = (status: ApprovalLink['status']): Tone => (status === 'active' ? 'good' : status === 'revoked' ? 'bad' : 'neutral');
  return (
    <section className="qc-panel" aria-labelledby="content-share">
      <Heading level={2} id="content-share">{t('portal.partners.content.share.heading')}</Heading>
      {d.deliveredOn ? (
        <>
          <Text tone="muted">{t('portal.partners.content.share.intro')}</Text>
          <ShareItemPicker d={d} selected={extra} onToggle={toggle} />
          <Checkbox label={t('portal.partners.content.share.email.toggle')} checked={byEmail} onChange={setByEmail} name="share-by-email" />
          {byEmail ? (
            <div data-testid="approval-email-form">
              <Field label={t('portal.partners.content.share.email.to')} hint={t('portal.partners.content.share.email.hint', { n: SHARE_MAX_RECIPIENTS })} error={formError}>
                {(id, describedBy) => <textarea id={id} name="share-recipients" rows={2} value={recipients} aria-describedby={describedBy} onChange={(e) => setRecipients(e.target.value)} />}
              </Field>
              <Field label={t('portal.partners.content.share.email.language')}>
                {(id) => (
                  <select id={id} name="share-locale" value={mailLocale} onChange={(e) => setMailLocale(e.target.value === 'ar' ? 'ar' : 'en')}>
                    <option value="en">{t('portal.partners.content.share.email.lang.en')}</option>
                    <option value="ar">{t('portal.partners.content.share.email.lang.ar')}</option>
                  </select>
                )}
              </Field>
            </div>
          ) : null}
          <div>
            <Button type="button" onClick={() => void create()} disabled={make.pending}>
              {byEmail ? t('portal.partners.content.share.email.create')
                : extra.length ? t('portal.partners.content.share.createMany', { n: 1 + extra.length }) : t('portal.partners.content.share.create')}
            </Button>
          </div>
        </>
      ) : <Text tone="muted">{t('portal.partners.content.share.notDelivered')}</Text>}
      <ContentError error={make.error ?? revoke.error} />
      {created ? <CreatedLink link={created} onDismiss={() => setCreated(null)} /> : null}
      <Heading level={3}>{t('portal.partners.content.share.listHeading')}</Heading>
      <Loadable resource={links}>
        {(list) => list.length === 0 ? <Text tone="muted">{t('portal.partners.content.share.none')}</Text> : (
          <ul className="qc-list" data-testid="approval-links">
            {list.map((l) => (
              <li key={l.id}>
                <span className="qc-inline">
                  <Badge tone={tone(l.status)} testId="approval-link-status">{t(`portal.partners.content.share.status.${l.status}`)}</Badge>
                  <small>{t('portal.partners.content.share.createdOn', { date: fmtDate(locale, l.createdAt.slice(0, 10)) })}</small>
                </span>
                <small className="qc-hint">
                  {t('portal.partners.content.share.expires', { date: fmtDate(locale, l.expiresAt.slice(0, 10)) })}
                  {' · '}
                  {t('portal.partners.content.share.itemCount', { n: l.deliverableIds.length })}
                  {' · '}
                  {l.lastViewedAt ? t('portal.partners.content.share.lastViewed', { date: fmtDate(locale, l.lastViewedAt.slice(0, 10)) }) : t('portal.partners.content.share.neverViewed')}
                  {' · '}
                  {t('portal.partners.content.share.responses', { n: l.responseCount })}
                </small>
                {l.responses.map((r) => (
                  <small key={`${r.respondedAt}:${r.clientName}:${r.approval}:${r.deliverableId}`} className="qc-hint">
                    {t('portal.partners.content.share.responseLine', {
                      name: r.clientName, decision: t(`portal.partners.content.approval.${r.approval}`), date: fmtDate(locale, r.respondedAt.slice(0, 10)),
                    })}
                    {r.comment ? ` · ${r.comment}` : ''}
                  </small>
                ))}
                {l.status === 'active' ? (
                  <div><Button type="button" onClick={() => void cancel(l.id)} disabled={revoke.pending}>{t('portal.partners.content.share.revoke')}</Button></div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Loadable>
    </section>
  );
}

/** Pick other delivered items of the same client to share on the same link. */
function ShareItemPicker({ d, selected, onToggle }: { d: DeliverableView; selected: string[]; onToggle: (id: string, on: boolean) => void }) {
  const { t } = useSurface();
  const api = useApi();
  const rows = useResource(`content:share-candidates:${d.clientId}`, () => api.deliverables({ clientId: d.clientId, kind: 'delivery', limit: 200 }).then((r) => r.data));
  return (
    <fieldset className="qc-panel" data-testid="approval-items">
      <legend>{t('portal.partners.content.share.items')}</legend>
      <Checkbox label={d.title} checked disabled onChange={() => undefined} />
      <Loadable resource={rows}>
        {(list) => {
          const others = shareCandidates(d, list);
          if (!others.length) return <Text tone="muted">{t('portal.partners.content.share.noOtherItems')}</Text>;
          const full = selected.length + 1 >= SHARE_MAX_ITEMS;
          return (
            <>
              <small className="qc-hint">{t('portal.partners.content.share.itemsHint', { n: SHARE_MAX_ITEMS })}</small>
              {others.map((r) => (
                <Checkbox key={r.id} name="share-item" label={r.title} checked={selected.includes(r.id)} disabled={full && !selected.includes(r.id)} onChange={(on) => onToggle(r.id, on)} />
              ))}
            </>
          );
        }}
      </Loadable>
    </fieldset>
  );
}

function CreatedLink({ link, onDismiss }: { link: ApprovalLinkCreated; onDismiss: () => void }) {
  const { t, locale } = useSurface();
  const field = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState<'yes' | 'no' | null>(null);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link.url);
      setCopied('yes');
    } catch {
      // clipboard blocked (no permission or insecure context): leave the link selected for a manual copy
      field.current?.select();
      setCopied('no');
    }
  };
  return (
    <div className="qc-panel" data-testid="approval-link-created">
      <Text>{t('portal.partners.content.share.showOnce')}</Text>
      <Field label={t('portal.partners.content.share.urlLabel')}>
        {(id) => <input id={id} ref={field} type="url" readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} />}
      </Field>
      <div className="qc-inline">
        <Button type="button" onClick={() => void copy()}>{t('portal.partners.content.share.copy')}</Button>
        <Button type="button" onClick={onDismiss}>{t('portal.partners.content.share.done')}</Button>
      </div>
      <output aria-live="polite">
        {copied === 'yes' ? t('portal.partners.content.share.copied') : copied === 'no' ? t('portal.partners.content.share.copyFailed') : null}
      </output>
      <Text tone="muted">{t('portal.partners.content.share.expires', { date: fmtDate(locale, link.expiresAt.slice(0, 10)) })}</Text>
      {link.emails?.length ? (
        <ul className="qc-list" data-testid="approval-email-results">
          {link.emails.map((e) => (
            <li key={e.to}>
              <Badge tone={e.status === 'sent' ? 'good' : 'bad'}>{t(`portal.partners.content.share.email.${e.status}`)}</Badge> {e.to}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function FileForm({ itemId, onDone }: { itemId: string; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const act = useAction((body: { url: string; label?: string }) => api.addFile(itemId, body));
  const ok = isHttps(url);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    if (await act.run({ url: url.trim(), ...(label.trim() ? { label: label.trim() } : {}) })) { setUrl(''); setLabel(''); onDone(); }
  };
  return (
    <form onSubmit={submit} className="qc-form-grid" aria-label={t('portal.partners.content.item.addFile')}>
      <Field label={t('portal.partners.content.item.fileUrl')} hint={t('portal.partners.content.httpsHint')} error={url && !ok ? t('portal.partners.content.httpsOnly') : null}>
        {(id, desc) => <input id={id} type="url" name="fileUrl" inputMode="url" value={url} aria-describedby={desc} onChange={(e) => setUrl(e.target.value)} />}
      </Field>
      <Field label={t('portal.partners.content.item.fileLabel')}>
        {(id) => <input id={id} name="fileLabel" maxLength={120} value={label} onChange={(e) => setLabel(e.target.value)} />}
      </Field>
      <div><Button type="submit" disabled={!ok || act.pending}>{t('portal.partners.content.item.addFile')}</Button></div>
      <ContentError error={act.error} />
    </form>
  );
}

function ActivityTable({ rows, members }: { rows: DeliverableDetail['activity']; members: readonly ContentMember[] }) {
  const { t, locale } = useSurface();
  // a client answer through an approval link is logged under the link, not a team member (US-18)
  const who = (id: string) => (id.startsWith('client_link:') ? t('portal.partners.content.activity.clientLink') : members.find((m) => m.userId === id)?.name ?? t('portal.partners.content.none'));
  const cols: Column<DeliverableDetail['activity'][number]>[] = [
    { key: 'at', header: t('portal.partners.content.activity.at'), render: (r) => <time dateTime={r.at}>{fmtDate(locale, r.at.slice(0, 10))}</time> },
    { key: 'field', header: t('portal.partners.content.activity.field'), render: (r) => r.field },
    { key: 'old', header: t('portal.partners.content.activity.old'), render: (r) => r.oldValue ?? t('portal.partners.content.none'), narrow: 'hide' },
    { key: 'new', header: t('portal.partners.content.activity.new'), render: (r) => r.newValue ?? t('portal.partners.content.none') },
    { key: 'by', header: t('portal.partners.content.activity.by'), render: (r) => who(r.userId), narrow: 'hide' },
    { key: 'reason', header: t('portal.partners.content.activity.reason'), render: (r) => r.reason ?? '', narrow: 'hide' },
  ];
  return <DataTable columns={cols} rows={rows} rowKey={(r) => r.id} empty={t('portal.partners.content.activity.empty')} caption={t('portal.partners.content.activity.caption')} />;
}

// ---------------------------------------------------------------- My Work

export function MyWorkPage() {
  return <WithBase>{(b) => <MyWorkBody base={b} />}</WithBase>;
}

function MyWorkBody({ base }: { base: Base }) {
  const { t } = useSurface();
  const api = useApi();
  const list = useResource('content:my-work', () => api.myWork());
  usePoll(list.reload, 5000);
  return (
    <Screen title={t('portal.partners.content.myWork.title')} subtitle={t('portal.partners.content.myWork.subtitle')} eyebrow={t('portal.partners.content.eyebrow')} testId="content-my-work">
      <ContentNav me={base.me} />
      <Loadable resource={list}>{(rows) => <ItemTable rows={rows} base={base} onChanged={list.reload} variant="compact" />}</Loadable>
    </Screen>
  );
}

// ---------------------------------------------------------------- backlog

export function BacklogPage() {
  return <WithBase>{(b) => <BacklogBody base={b} />}</WithBase>;
}

function BacklogBody({ base }: { base: Base }) {
  const { t } = useSurface();
  const api = useApi();
  const [sp, setSp] = useSearchParams();
  const clients = base.clients.filter((c) => c.active);
  const asked = sp.get('client');
  const clientId = clients.some((c) => c.id === asked) ? asked! : clients[0]?.id ?? '';
  const board = useResource<BacklogBoard | null>(`content:backlog:${clientId}`, () => (clientId ? api.backlog(clientId) : Promise.resolve(null)));
  const link = (extra: Filters) => listHref({ client: clientId, ...extra });

  return (
    <Screen title={t('portal.partners.content.backlog.title')} subtitle={t('portal.partners.content.backlog.subtitle')} eyebrow={t('portal.partners.content.eyebrow')} testId="content-backlog">
      <ContentNav me={base.me} />
      {clients.length === 0 ? <EmptyState message={t('portal.partners.content.backlog.noClients')} /> : (
        <>
          <div className="qx-toolbar">
            <FilterSelect label={t('portal.partners.content.backlog.client')} name="client" value={clientId} options={clients.map((c) => ({ value: c.id, label: c.name }))}
              onChange={(v) => setSp(setParam(sp, 'client', v))} />
          </div>
          <Loadable resource={board}>
            {(b) => !b ? null : (
              <>
                <div className="qx-grid">
                  <Stat label={t('portal.partners.content.backlog.open')} testId="backlog-open" value={<Link to={link({ backlog: 'true' })}>{b.open}</Link>} />
                  <Stat label={t('portal.partners.content.backlog.ready')} testId="backlog-ready" value={<Link to={link({ backlog: 'true', ready: 'true' })}>{b.ready}</Link>} />
                  <Stat label={t('portal.partners.content.backlog.inProduction')} testId="backlog-production" value={<Link to={link({ backlog: 'true', ready: 'false' })}>{b.inProduction}</Link>} />
                  <Stat label={t('portal.partners.content.backlog.cleared', { month: b.month })} testId="backlog-cleared" value={<Link to={link({ clearedIn: b.month })}>{b.clearedThisMonth}</Link>} />
                  <Stat label={t('portal.partners.content.backlog.pushed')} testId="backlog-pushed" value={b.pushedToLater} />
                </div>
                <section className="qc-panel" aria-labelledby="content-buckets">
                  <Heading level={2} id="content-buckets">{t('portal.partners.content.backlog.buckets')}</Heading>
                  <div className="qx-grid">
                    {BACKLOG_BUCKETS.map((k) => (
                      <Stat key={k} label={t(`portal.partners.content.bucket.${k}`)} testId={`bucket-${k}`}
                        value={<Link to={link({ backlog: 'true', bucket: k })}>{b.buckets[k]}</Link>} />
                    ))}
                  </div>
                </section>
                <section className="qc-panel" aria-labelledby="content-bytype">
                  <Heading level={2} id="content-bytype">{t('portal.partners.content.backlog.byType')}</Heading>
                  <DataTable
                    rows={base.catalog.contentTypes.filter((x) => x.active).map((x) => ({ key: x.key, label: x.label, c: b.byType[x.key] ?? { open: 0, ready: 0, inProduction: 0 } }))}
                    rowKey={(r) => r.key} empty={t('portal.partners.content.list.empty')} caption={t('portal.partners.content.backlog.byType')}
                    columns={[
                      { key: 'type', header: t('portal.partners.content.col.type'), render: (r) => r.label },
                      { key: 'open', header: t('portal.partners.content.backlog.open'), render: (r) => <Link to={link({ backlog: 'true', type: r.key })}>{r.c.open}</Link> },
                      { key: 'ready', header: t('portal.partners.content.backlog.ready'), render: (r) => <Link to={link({ backlog: 'true', ready: 'true', type: r.key })}>{r.c.ready}</Link> },
                      { key: 'prod', header: t('portal.partners.content.backlog.inProduction'), render: (r) => <Link to={link({ backlog: 'true', ready: 'false', type: r.key })}>{r.c.inProduction}</Link> },
                    ]}
                  />
                </section>
              </>
            )}
          </Loadable>
          <BacklogLoadForm base={base} clientId={clientId} onLoaded={() => board.reload()} />
          {base.me.can.editDeliverables ? <BacklogCsvForm clientId={clientId} onLoaded={() => board.reload()} /> : null}
        </>
      )}
    </Screen>
  );
}

interface LoadRow { contentType: string; month: string; count: number }
function BacklogLoadForm({ base, clientId, onLoaded }: { base: Base; clientId: string; onLoaded: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const types = base.catalog.contentTypes.filter((x) => x.active);
  const lastMonth = shiftMonth(base.me.today.slice(0, 7), -1);
  const [rows, setRows] = useState<LoadRow[]>([{ contentType: types[0]?.key ?? '', month: lastMonth, count: 1 }]);
  const act = useAction((body: { clientId: string; rows: LoadRow[] }) => api.loadBacklog(body, newIdempotencyKey()));
  const valid = rows.length > 0 && rows.every((r) => r.contentType && /^\d{4}-\d{2}$/.test(r.month) && r.count >= 1 && r.count <= 500);
  const set = (i: number, patch: Partial<LoadRow>) => setRows((list) => list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || !clientId) return;
    const res = await act.run({ clientId, rows });
    if (res) { onLoaded(); setRows([{ contentType: types[0]?.key ?? '', month: lastMonth, count: 1 }]); }
  };
  return (
    <section className="qc-panel" aria-labelledby="content-load">
      <Heading level={2} id="content-load">{t('portal.partners.content.backlog.load')}</Heading>
      <Text tone="muted">{t('portal.partners.content.backlog.loadHint')}</Text>
      <form onSubmit={submit} noValidate>
        {rows.map((r, i) => (
          <div className="qc-form-grid" key={i}>
            <Field label={t('portal.partners.content.col.type')}>
              {(id) => <select id={id} value={r.contentType} onChange={(e) => set(i, { contentType: e.target.value })}>
                {types.map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>}
            </Field>
            <Field label={t('portal.partners.content.backlog.loadMonth')}>
              {(id) => <input id={id} type="month" value={r.month} onChange={(e) => set(i, { month: e.target.value })} />}
            </Field>
            <Field label={t('portal.partners.content.backlog.loadCount')}>
              {(id) => <input id={id} type="number" min={1} max={500} step={1} value={r.count} onChange={(e) => set(i, { count: Number(e.target.value) })} />}
            </Field>
            <div>
              <Button type="button" disabled={rows.length === 1} onClick={() => setRows((list) => list.filter((_, j) => j !== i))}>{t('portal.partners.content.backlog.loadRemove')}</Button>
            </div>
          </div>
        ))}
        <div className="qx-screen__actions">
          <Button type="button" onClick={() => setRows((list) => [...list, { contentType: types[0]?.key ?? '', month: lastMonth, count: 1 }])} disabled={rows.length >= 100}>{t('portal.partners.content.backlog.loadAdd')}</Button>
          <Button type="submit" variant="primary" disabled={!valid || act.pending || !clientId}>{t('portal.partners.content.backlog.loadSubmit')}</Button>
        </div>
      </form>
      <ContentError error={act.error} />
      {act.result ? <Notice testId="backlog-loaded">{t('portal.partners.content.backlog.loaded', { n: act.result.created })}</Notice> : null}
    </section>
  );
}

/** Backlog CSV upload (US-21): parse in the browser, dry run, review, then load. Rules and checks are on the server. */
function BacklogCsvForm({ clientId, onLoaded }: { clientId: string; onLoaded: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [name, setName] = useState('');
  const [dry, setDry] = useState<BacklogImportReport | null>(null);
  const [done, setDone] = useState<BacklogImportReport | null>(null);
  const run = useAction((dryRun: boolean) => api.importBacklog({ dryRun, ...(clientId ? { clientId } : {}), rows }, dryRun ? undefined : newIdempotencyKey()));
  useEffect(() => { setDry(null); setDone(null); }, [clientId]);
  const pick = async (file: File | undefined) => {
    setDry(null); setDone(null);
    if (!file) { setRows([]); setName(''); return; }
    setRows(parseCsv(await file.text()));
    setName(file.name);
  };
  const dryRunClick = async () => { const r = await run.run(true); if (r) { setDry(r); setDone(null); } };
  const commitClick = async () => { const r = await run.run(false); if (r) { setDone(r); if (r.committed) onLoaded(); } };
  const report = done ?? dry;
  return (
    <section className="qc-panel" aria-labelledby="content-backlog-csv" data-testid="backlog-csv">
      <Heading level={2} id="content-backlog-csv">{t('portal.partners.content.backlog.csv')}</Heading>
      <Text tone="muted">{t('portal.partners.content.backlog.csvHint')}</Text>
      <Field label={t('portal.partners.content.backlog.csvFile')} hint={name ? t('portal.partners.content.backlog.csvRows', { n: rows.length, name }) : undefined}>
        {(id, d) => <input id={id} type="file" accept=".csv,text/csv" aria-describedby={d} onChange={(e) => void pick(e.target.files?.[0])} />}
      </Field>
      <div className="qx-screen__actions">
        <Button type="button" disabled={rows.length === 0 || run.pending} onClick={() => void dryRunClick()}>{t('portal.partners.content.import.dryRun')}</Button>
        <Button type="button" variant="primary" disabled={!dry || dry.errors.length > 0 || dry.counts.items === 0 || !!done || run.pending} onClick={() => void commitClick()}>{t('portal.partners.content.backlog.csvCommit')}</Button>
      </div>
      <ContentError error={run.error} />
      {report ? (
        <div data-testid="backlog-csv-report">
          <Text>{t('portal.partners.content.backlog.csvCounts', { rows: report.counts.rows, items: report.counts.items, skipped: report.counts.skippedDuplicates })}</Text>
          {report.committed ? <Notice tone="good" testId="backlog-csv-committed">{t('portal.partners.content.backlog.csvCommitted')}</Notice> : null}
          {report.errors.length > 0 ? (
            <DataTable rows={report.errors} rowKey={(e) => `${e.row}:${e.message}`} empty="" caption={t('portal.partners.content.import.errors')}
              columns={[
                { key: 'row', header: t('portal.partners.content.import.row'), render: (e) => e.row },
                { key: 'message', header: t('portal.partners.content.import.message'), render: (e) => e.message },
              ]} />
          ) : <Text tone="muted">{t('portal.partners.content.import.noErrors')}</Text>}
          {report.duplicates.length > 0 ? (
            <DataTable rows={report.duplicates} rowKey={(e) => `${e.row}:${e.key}`} empty="" caption={t('portal.partners.content.import.duplicates')}
              columns={[
                { key: 'row', header: t('portal.partners.content.import.row'), render: (e) => e.row },
                { key: 'key', header: t('portal.partners.content.import.key'), render: (e) => e.key },
              ]} />
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------- scope

export function ScopePage() {
  return <WithBase>{(b) => <ScopeBody base={b} />}</WithBase>;
}

function ScopeBody({ base }: { base: Base }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const [sp, setSp] = useSearchParams();
  const asked = sp.get('month') ?? '';
  const month = /^\d{4}-\d{2}$/.test(asked) ? asked : base.me.today.slice(0, 7);
  const report = useResource<ScopeReport>(`content:scope:${month}`, () => api.scopeReport(month));
  const scope = useResource<ScopeRow[]>(`content:scope-rows:${month}`, () => api.scope(month));
  const trend = useResource<TrendReport>(`content:trend:${month}`, () => api.trend(month, 6));
  const reloadAll = () => { report.reload(); scope.reload(); trend.reload(); };

  return (
    <Screen title={t('portal.partners.content.scope.title')} subtitle={t('portal.partners.content.scope.subtitle')} eyebrow={t('portal.partners.content.eyebrow')} testId="content-scope"
      actions={<a className="qm-button" data-variant="secondary" href={exportHref('scope', { month }, 'html', locale)} target="_blank" rel="noopener">{t('portal.partners.content.export.pdf')}</a>}>
      <ContentNav me={base.me} />
      <div className="qx-toolbar">
        <Field label={t('portal.partners.content.scope.month')}>
          {(id) => <input id={id} type="month" name="month" value={month} onChange={(e) => { if (e.target.value) setSp(setParam(sp, 'month', e.target.value)); }} />}
        </Field>
      </div>
      <Loadable resource={report}>
        {(r) => (
          <>
            <ScopeGroup title={t('portal.partners.content.scope.posting')} rows={r.posting} types={r.contentTypes} typeLabel={(k) => catalogLabel(base.catalog.contentTypes, k)} month={month} empty={t('portal.partners.content.scope.emptyPosting')} />
            <ScopeGroup title={t('portal.partners.content.scope.delivery')} rows={r.delivery} types={r.contentTypes} typeLabel={(k) => catalogLabel(base.catalog.contentTypes, k)} month={month} empty={t('portal.partners.content.scope.emptyDelivery')} />
          </>
        )}
      </Loadable>
      <Loadable resource={scope}>
        {(rows) => <ScopeEditor base={base} month={month} rows={rows} onSaved={reloadAll} />}
      </Loadable>
      <section className="qc-panel" aria-labelledby="content-trend">
        <Heading level={2} id="content-trend">{t('portal.partners.content.scope.trend')}</Heading>
        <Text tone="muted">{t('portal.partners.content.scope.trendHint')}</Text>
        <Loadable resource={trend}>
          {(tr) => (
            <DataTable
              rows={tr.clients}
              rowKey={(c) => c.clientId}
              empty={t('portal.partners.content.scope.emptyTrend')}
              caption={t('portal.partners.content.scope.trend')}
              columns={[
                { key: 'client', header: t('portal.partners.content.col.client'), render: (c) => c.clientName },
                ...tr.months.map((m) => ({
                  key: m, header: fmtMonth(locale, m),
                  render: (c: TrendReport['clients'][number]) => {
                    const p = c.points.find((x) => x.month === m);
                    return p && p.pctDone !== null ? t('portal.partners.content.percent', { n: p.pctDone }) : t('portal.partners.content.none');
                  },
                })),
              ]}
            />
          )}
        </Loadable>
      </section>
    </Screen>
  );
}

function ScopeGroup({ title, rows, types, typeLabel, month, empty }: {
  title: string; rows: ScopeReport['posting']; types: string[]; typeLabel: (k: string) => string; month: string; empty: string;
}) {
  const { t } = useSurface();
  const cols: Column<ScopeReport['posting'][number]>[] = [
    { key: 'client', header: t('portal.partners.content.col.client'), render: (r) => <Link to={listHref({ client: r.clientId, month })}>{r.clientName}</Link> },
    ...types.flatMap((k) => [
      { key: `${k}-scope`, header: t('portal.partners.content.scope.colTypeScope', { type: typeLabel(k) }), render: (r: ScopeReport['posting'][number]) => r.types[k]?.scope ?? 0 },
      { key: `${k}-done`, header: t('portal.partners.content.scope.colTypeDone', { type: typeLabel(k) }), render: (r: ScopeReport['posting'][number]) => r.types[k]?.done ?? 0 },
    ]),
    { key: 'total', header: t('portal.partners.content.scope.total'), render: (r) => r.total.scope },
    { key: 'done', header: t('portal.partners.content.scope.done'), render: (r) => r.total.done },
    { key: 'balance', header: t('portal.partners.content.scope.balance'), render: (r) => r.balance },
    { key: 'pct', header: t('portal.partners.content.scope.pct'), render: (r) => (r.pctDone === null ? t('portal.partners.content.none') : t('portal.partners.content.percent', { n: r.pctDone })) },
    { key: 'cleared', header: t('portal.partners.content.scope.backlogCleared'), render: (r) => r.backlogCleared, narrow: 'hide' },
    { key: 'open', header: t('portal.partners.content.scope.openBacklog'), render: (r) => <Link to={listHref({ client: r.clientId, backlog: 'true' })}>{r.openBacklog}</Link>, narrow: 'hide' },
  ];
  return (
    <section className="qc-panel" aria-label={title}>
      <Heading level={2}>{title}</Heading>
      <DataTable columns={cols} rows={rows} rowKey={(r) => r.clientId} empty={empty} caption={title} />
    </section>
  );
}

/** Scope per client and month. Editing one month never changes another (per-month scope). */
function ScopeEditor({ base, month, rows, onSaved }: { base: Base; month: string; rows: ScopeRow[]; onSaved: () => void }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const clients = base.clients.filter((c) => c.active);
  const [clientId, setClientId] = useState(clients[0]?.id ?? '');
  const types = base.catalog.contentTypes.filter((x) => x.active);
  const [rev, setRev] = useState(0);
  const [confirmCopy, setConfirmCopy] = useState(false);
  const [clearOthers, setClearOthers] = useState(false);
  const save = useAction((items: { contentType: string; quantity: number }[]) => api.saveScope({ clientId, month, items }));
  const copy = useAction((mode: 'merge' | 'replace') => api.copyScope({ fromMonth: shiftMonth(month, -1), clientId, mode }));
  const current = (k: string) => rows.find((r) => r.clientId === clientId && r.month === month && r.contentType === k)?.quantity ?? 0;
  const [qty, setQty] = useState<Record<string, number>>({});
  useEffect(() => {
    setQty(Object.fromEntries(types.map((x) => [x.key, current(x.key)])));
    // reset the inputs when the client, the month or the saved values change
  }, [clientId, month, rev, rows]);
  if (clients.length === 0) return null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const items = types.map((x) => ({ contentType: x.key, quantity: Math.max(0, Math.floor(qty[x.key] ?? 0)) }));
    if (await save.run(items)) onSaved();
  };
  const doCopy = async () => {
    if (await copy.run(clearOthers ? 'replace' : 'merge')) {
      setRev((n) => n + 1);
      setConfirmCopy(false);
      setClearOthers(false);
      onSaved();
    }
  };
  return (
    <section className="qc-panel" aria-labelledby="content-scope-editor" data-testid="content-scope-editor">
      <Heading level={2} id="content-scope-editor">{t('portal.partners.content.scope.editor')}</Heading>
      <form onSubmit={submit} noValidate>
        <div className="qc-form-grid">
          <Field label={t('portal.partners.content.backlog.client')}>
            {(id) => <select id={id} value={clientId} onChange={(e) => setClientId(e.target.value)}>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>}
          </Field>
          {types.map((x) => (
            <Field key={x.key} label={t('portal.partners.content.scope.qty', { type: x.label })}>
              {(id) => <input id={id} type="number" min={0} max={10000} step={1} value={qty[x.key] ?? 0} onChange={(e) => setQty((q) => ({ ...q, [x.key]: Number(e.target.value) }))} />}
            </Field>
          ))}
        </div>
        <div className="qx-screen__actions">
          <Button type="submit" variant="primary" disabled={save.pending}>{t('portal.partners.content.scope.save')}</Button>
          <Button type="button" onClick={() => setConfirmCopy(true)} disabled={copy.pending || confirmCopy}>{t('portal.partners.content.scope.copyLast')}</Button>
        </div>
        {confirmCopy ? (
          <div className="qc-confirm" data-testid="scope-copy-confirm">
            <Text>{t('portal.partners.content.scope.copyConfirm', { from: fmtMonth(locale, shiftMonth(month, -1), true), to: fmtMonth(locale, month, true) })}</Text>
            <Checkbox name="clearOthers" label={t('portal.partners.content.scope.copyClear')} checked={clearOthers} onChange={setClearOthers} disabled={copy.pending} />
            {clearOthers ? null : <Text tone="muted">{t('portal.partners.content.scope.copyMergeHint')}</Text>}
            <div className="qx-screen__actions">
              <Button type="button" variant="primary" onClick={() => void doCopy()} disabled={copy.pending}>{t('portal.partners.content.scope.copyDo')}</Button>
              <Button type="button" onClick={() => { setConfirmCopy(false); setClearOthers(false); }} disabled={copy.pending}>{t('portal.partners.content.cancel')}</Button>
            </div>
          </div>
        ) : null}
      </form>
      <ContentError error={save.error ?? copy.error} />
      {save.result ? <Notice testId="scope-saved">{t('portal.partners.content.scope.saved')}</Notice> : null}
      {base.me.can.editDeliverables && clientId ? (
        <GenerateDrafts key={`${clientId}:${month}`} clientId={clientId} month={month} onDone={onSaved} />
      ) : null}
    </section>
  );
}

/** US-06: create the drafts the scope still needs for one client and month, optionally spread across the month. */
function GenerateDrafts({ clientId, month, onDone }: { clientId: string; month: string; onDone: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [spread, setSpread] = useState(false);
  const [made, setMade] = useState<number | null>(null);
  const act = useAction(() => api.generate({ clientId, month, spread }));
  const run = async () => {
    const res = await act.run();
    if (res) { setMade(res.created); onDone(); }
  };
  return (
    <div className="qc-generate" data-testid="content-generate">
      <Heading level={3}>{t('portal.partners.content.scope.generate.heading')}</Heading>
      <Text tone="muted">{t('portal.partners.content.scope.generate.hint')}</Text>
      <Checkbox name="spread" label={t('portal.partners.content.scope.generate.spread')} checked={spread} onChange={setSpread} disabled={act.pending} />
      <div className="qx-screen__actions">
        <Button type="button" onClick={() => void run()} disabled={act.pending}>{t('portal.partners.content.scope.generate.button')}</Button>
      </div>
      <ContentError error={act.error} />
      {made !== null ? <Notice testId="scope-generated">{generatedMessage(t, made)}</Notice> : null}
    </div>
  );
}

// ---------------------------------------------------------------- notifications (US-30)

export function NotificationsPage() {
  return <WithBase>{(b) => <NotificationsBody base={b} />}</WithBase>;
}

function NotificationsBody({ base }: { base: Base }) {
  const { t } = useSurface();
  const api = useApi();
  const list = useResource<NotificationList>(NOTIFICATIONS_KEY, () => api.notifications());
  return (
    <Screen title={t('portal.partners.content.notifications.title')} subtitle={t('portal.partners.content.notifications.subtitle')} eyebrow={t('portal.partners.content.eyebrow')} testId="content-notifications">
      <ContentNav me={base.me} />
      <Loadable resource={list}>
        {(env) => (
          <>
            <Text tone="muted" data-testid="notifications-unread">
              {t(env.meta.unread === 1 ? 'portal.partners.content.notifications.unread.one' : 'portal.partners.content.notifications.unread.other', { n: env.meta.unread })}
            </Text>
            {env.data.length === 0 ? <EmptyState message={t('portal.partners.content.notifications.empty')} /> : (
              <ul className="qc-notes" aria-label={t('portal.partners.content.notifications.title')}>
                {env.data.map((n) => <NotificationItem key={n.id} n={n} onChanged={list.reload} />)}
              </ul>
            )}
          </>
        )}
      </Loadable>
      <MyWhatsAppPanel />
    </Screen>
  );
}

/** Language picker for WhatsApp templates (US-30). */
function WhatsAppLanguageField({ value, onChange }: { value: WhatsAppLanguage; onChange: (v: WhatsAppLanguage) => void }) {
  const { t } = useSurface();
  return (
    <Field label={t('portal.partners.content.settings.whatsapp.language')}>
      {(id) => <select id={id} name="whatsappLanguage" value={value} onChange={(e) => onChange(e.target.value === 'ar' ? 'ar' : 'en')}>
        <option value="en">{t('portal.partners.content.settings.whatsapp.languageEn')}</option>
        <option value="ar">{t('portal.partners.content.settings.whatsapp.languageAr')}</option>
      </select>}
    </Field>
  );
}

/** The signed-in team member's own WhatsApp number, consent and language (US-30). Hidden for users with no member row. */
function MyWhatsAppPanel() {
  const api = useApi();
  const mine = useResource<MyWhatsApp>('content:me:whatsapp', () => api.myWhatsApp());
  return <Loadable resource={mine}>{(m) => (m.hasMember ? <MyWhatsAppForm key={`${m.whatsappPhone}:${m.whatsappOptIn}:${m.whatsappLanguage}`} initial={m} onSaved={mine.reload} /> : null)}</Loadable>;
}

function MyWhatsAppForm({ initial, onSaved }: { initial: MyWhatsApp; onSaved: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [phone, setPhone] = useState(initial.whatsappPhone ?? '');
  const [optIn, setOptIn] = useState(initial.whatsappOptIn);
  const [language, setLanguage] = useState<WhatsAppLanguage>(initial.whatsappLanguage);
  const save = useAction(() => api.saveMyWhatsApp({ whatsappPhone: phone.trim() ? phone.trim() : null, whatsappOptIn: phone.trim() ? optIn : false, whatsappLanguage: language }));
  const bad = phone.trim() !== '' && !isWhatsAppNumber(phone);
  const ok = whatsAppOk(phone, optIn);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (ok && await save.run()) onSaved();
  };
  return (
    <section className="qc-panel" aria-labelledby="content-my-whatsapp" data-testid="content-my-whatsapp">
      <Heading level={2} id="content-my-whatsapp">{t('portal.partners.content.notifications.whatsapp.heading')}</Heading>
      <Text tone="muted">{t('portal.partners.content.notifications.whatsapp.hint')}</Text>
      {!initial.agencyEnabled ? <Notice>{t('portal.partners.content.notifications.whatsapp.agencyOff')}</Notice> : null}
      {initial.agencyEnabled && !initial.available ? <Notice tone="warn">{t('portal.partners.content.settings.whatsapp.unavailable')}</Notice> : null}
      <form onSubmit={submit} noValidate className="qc-form-grid">
        <Field label={t('portal.partners.content.settings.whatsapp.phone')} hint={t('portal.partners.content.notifications.whatsapp.phoneHint')}
          error={bad ? t('portal.partners.content.settings.whatsapp.phoneInvalid') : null}>
          {(id, describedBy) => <input id={id} type="tel" name="whatsappPhone" inputMode="tel" autoComplete="tel" dir="ltr" maxLength={40}
            placeholder="+971 50 123 4567" value={phone} aria-describedby={describedBy} onChange={(e) => { setPhone(e.target.value); if (!e.target.value.trim()) setOptIn(false); }} />}
        </Field>
        <Checkbox name="whatsappOptIn" label={t('portal.partners.content.notifications.whatsapp.optIn')} checked={optIn} disabled={phone.trim() === ''} onChange={setOptIn} />
        <WhatsAppLanguageField value={language} onChange={setLanguage} />
        {language === 'ar' && initial.available && !initial.arabicAvailable ? <Text tone="muted">{t('portal.partners.content.settings.whatsapp.arabicPending')}</Text> : null}
        <div><Button type="submit" variant="primary" disabled={!ok || save.pending}>{t('portal.partners.content.save')}</Button></div>
      </form>
      <ContentError error={save.error} />
      {save.result ? <Notice testId="my-whatsapp-saved">{t('portal.partners.content.notifications.whatsapp.saved')}</Notice> : null}
    </section>
  );
}

const KIND_TONE: Record<NotificationKind, 'accent' | 'warn' | 'bad' | 'neutral'> = { assigned: 'accent', at_risk: 'warn', changes_requested: 'bad', digest: 'neutral' };

/** One notification. Unread rows are marked by a badge, a heavier weight and an accent edge (not colour alone). */
function NotificationItem({ n, onChanged }: { n: NotificationRow; onChanged: () => void }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const act = useAction(() => api.markNotificationRead(n.id));
  const unread = n.readAt === null;
  const kind = (NOTIFICATION_KINDS as readonly string[]).includes(n.kind) ? n.kind : 'digest';
  const p = n.params;
  const title = n.kind === 'digest'
    ? t('portal.partners.content.notifications.digest.title')
    : String(p.title ?? t(`portal.partners.content.notifications.kind.${kind}`));
  const detail = n.kind === 'digest'
    ? t('portal.partners.content.notifications.digest.body', {
      dueToday: p.dueToday ?? 0, overdue: p.overdue ?? 0, atRisk: p.atRisk ?? 0, backlogNotScheduled: p.backlogNotScheduled ?? 0,
    })
    : [p.clientName, p.workingDate ? fmtDate(locale, String(p.workingDate)) : null].filter(Boolean).join(' · ');
  return (
    <li className="qc-note" data-unread={unread ? 'true' : 'false'} data-kind={n.kind} data-testid="content-notification">
      <span className="qc-inline">
        {unread ? <Badge tone="accent" testId="notification-unread">{t('portal.partners.content.notifications.unreadBadge')}</Badge> : null}
        <Badge tone={KIND_TONE[kind]}>{t(`portal.partners.content.notifications.kind.${kind}`)}</Badge>
      </span>
      <span className="qc-stack">
        {n.deliverableId ? <Link to={`/content/items/${encodeURIComponent(n.deliverableId)}`}>{title}</Link> : <strong>{title}</strong>}
        {detail ? <small>{detail}</small> : null}
        <time dateTime={n.createdAt}>{fmtDate(locale, n.createdAt.slice(0, 10))}</time>
      </span>
      {unread ? (
        <Button type="button" disabled={act.pending} onClick={async () => { if (await act.run()) onChanged(); }}>
          {t('portal.partners.content.notifications.markRead')}
        </Button>
      ) : null}
      <ContentError error={act.error} />
    </li>
  );
}

// ---------------------------------------------------------------- Daily Grid

export function DailyGridPage() {
  return <WithBase>{(b) => <GridBody base={b} />}</WithBase>;
}

const validDay = (v: string | null) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);

function GridBody({ base }: { base: Base }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const [sp, setSp] = useSearchParams();
  const from = validDay(sp.get('from')) ?? base.me.today;
  const grid = useResource<DailyGrid>(gridKey(from), () => api.dailyGrid(from, GRID_DAYS));
  const move = (days: number) => setSp(setParam(sp, 'from', shiftDay(from, days)));
  return (
    <Screen title={t('portal.partners.content.grid.title')} subtitle={t('portal.partners.content.grid.subtitle')} eyebrow={t('portal.partners.content.eyebrow')} testId="content-grid">
      <ContentNav me={base.me} />
      <div className="qx-toolbar">
        <Button type="button" onClick={() => move(-GRID_DAYS)}>{t('portal.partners.content.grid.prev')}</Button>
        <Field label={t('portal.partners.content.grid.from')}>
          {(id) => <input id={id} type="date" name="from" value={from} onChange={(e) => { if (validDay(e.target.value)) setSp(setParam(sp, 'from', e.target.value)); }} />}
        </Field>
        <Button type="button" onClick={() => move(GRID_DAYS)}>{t('portal.partners.content.grid.next')}</Button>
        <Button type="button" onClick={() => setSp(setParam(sp, 'from', ''))} disabled={!sp.get('from')}>{t('portal.partners.content.grid.today')}</Button>
      </div>
      <Loadable resource={grid}>
        {(g) => (
          <>
            <Text tone="muted">{t('portal.partners.content.grid.range', { from: fmtDate(locale, g.from), to: fmtDate(locale, g.to) })}</Text>
            {g.rows.length === 0 ? <EmptyState message={t('portal.partners.content.grid.empty')} /> : (
              <div className="qx-table-wrap qc-grid-wrap" role="region" tabIndex={0} aria-label={t('portal.partners.content.grid.region')}>
                <table className="qx-table qc-grid" data-testid="content-grid-table">
                  <caption className="qx-sr">{t('portal.partners.content.grid.caption')}</caption>
                  <thead>
                    <tr>
                      <th scope="col" className="qc-sticky">{t('portal.partners.content.grid.client')}</th>
                      {g.dates.map((d) => {
                        const today = d === g.today;
                        return (
                          <th key={d} scope="col" data-today={today ? 'true' : undefined} aria-current={today ? 'date' : undefined}>
                            <span className="qc-day">{weekdayOf(locale, d)}</span>
                            <b>{fmtDate(locale, d)}</b>
                            {today ? <Badge tone="accent">{t('portal.partners.content.grid.todayLabel')}</Badge> : null}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {g.rows.map((r) => (
                      <tr key={r.clientId}>
                        <th scope="row" className="qc-sticky">{r.clientName}</th>
                        {r.cells.map((c) => (
                          <td key={c.date} data-today={c.date === g.today ? 'true' : undefined}>
                            {c.total > 0 ? (
                              <Link className="qc-cell" data-state={c.state} to={listHref({ client: r.clientId, from: c.date, to: c.date })}
                                aria-label={t('portal.partners.content.grid.cellLabel', { client: r.clientName, date: fmtDate(locale, c.date), n: c.total, state: t(`portal.partners.content.grid.state.${c.state}`) })}>
                                <b>{c.total}</b>
                                <span>{t(`portal.partners.content.grid.state.${c.state}`)}</span>
                              </Link>
                            ) : (
                              <span className="qc-cell" data-state="empty"><span>{t('portal.partners.content.grid.state.empty')}</span></span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ul className="qc-legend" aria-label={t('portal.partners.content.grid.legend')}>
              {(['done', 'upcoming', 'overdue', 'empty'] as const).map((s) => (
                <li key={s}><i className="qc-swatch" data-state={s} aria-hidden="true" />{t(`portal.partners.content.grid.state.${s}`)}</li>
              ))}
            </ul>
          </>
        )}
      </Loadable>
    </Screen>
  );
}

// ---------------------------------------------------------------- dashboard

export function DashboardPage() {
  return <WithBase>{(b) => <DashboardBody base={b} />}</WithBase>;
}

function DashboardBody({ base }: { base: Base }) {
  const { t, locale } = useSurface();
  const api = useApi();
  const [sp, setSp] = useSearchParams();
  const asked = sp.get('month') ?? '';
  const month = /^\d{4}-\d{2}$/.test(asked) ? asked : base.me.today.slice(0, 7);
  const all = base.me.role !== 'account_manager' || sp.get('all') === 'true';
  const dash = useResource<Dashboard>(dashKey(month, all), () => api.dashboard(month, all));
  const isAm = base.me.role === 'account_manager';
  const m = { month };

  return (
    <Screen title={t('portal.partners.content.dash.title')} subtitle={t('portal.partners.content.dash.subtitle')} eyebrow={t('portal.partners.content.eyebrow')} testId="content-dashboard">
      <ContentNav me={base.me} />
      <div className="qx-toolbar">
        <Field label={t('portal.partners.content.dash.month')}>
          {(id) => <input id={id} type="month" name="month" value={month} onChange={(e) => { if (e.target.value) setSp(setParam(sp, 'month', e.target.value)); }} />}
        </Field>
        {isAm ? (
          <Checkbox name="all" label={t('portal.partners.content.dash.allClients')} checked={sp.get('all') === 'true'}
            onChange={(v) => setSp(setParam(sp, 'all', v ? 'true' : ''))} />
        ) : null}
      </div>
      <Loadable resource={dash}>
        {(d) => (
          <>
            <section className="qc-panel" aria-labelledby="kpi-posting">
              <Heading level={2} id="kpi-posting">{t('portal.partners.content.dash.posting')}</Heading>
              <div className="qx-grid">
                <Stat label={t('portal.partners.content.dash.scope')} testId="kpi-posting-scope" value={d.kpis.posting.scope} />
                <Stat label={t('portal.partners.content.dash.planned')} testId="kpi-posting-planned" value={<Link to={listHref({ ...m, kind: 'posting' })}>{d.kpis.posting.planned}</Link>} />
                <Stat label={t('portal.partners.content.dash.done')} testId="kpi-posting-done" value={<Link to={listHref({ ...m, kind: 'posting', done: 'true' })}>{d.kpis.posting.done}</Link>} />
                <Stat label={t('portal.partners.content.dash.pending')} testId="kpi-posting-pending" value={<Link to={listHref({ ...m, kind: 'posting', done: 'false' })}>{d.kpis.posting.pending}</Link>} />
                <Stat label={t('portal.partners.content.dash.overdue')} testId="kpi-posting-overdue" value={<Link to={listHref({ ...m, kind: 'posting', status: 'overdue' })}>{d.kpis.posting.overdue}</Link>} />
                <Stat label={t('portal.partners.content.dash.atRisk')} testId="kpi-posting-risk" value={<Link to={listHref({ ...m, kind: 'posting', status: 'at_risk' })}>{d.kpis.posting.atRisk}</Link>} />
                <Stat label={t('portal.partners.content.dash.partial')} testId="kpi-posting-partial" value={<Link to={listHref({ ...m, kind: 'posting', partial: 'true' })}>{d.kpis.posting.partial}</Link>} />
              </div>
            </section>
            <section className="qc-panel" aria-labelledby="kpi-delivery">
              <Heading level={2} id="kpi-delivery">{t('portal.partners.content.dash.delivery')}</Heading>
              <div className="qx-grid">
                <Stat label={t('portal.partners.content.dash.scope')} testId="kpi-delivery-scope" value={d.kpis.delivery.scope} />
                <Stat label={t('portal.partners.content.dash.planned')} testId="kpi-delivery-planned" value={<Link to={listHref({ ...m, kind: 'delivery' })}>{d.kpis.delivery.planned}</Link>} />
                <Stat label={t('portal.partners.content.dash.delivered')} testId="kpi-delivery-delivered" value={<Link to={listHref({ ...m, kind: 'delivery', done: 'true' })}>{d.kpis.delivery.delivered}</Link>} />
                <Stat label={t('portal.partners.content.dash.approved')} testId="kpi-delivery-approved" value={<Link to={listHref({ ...m, kind: 'delivery', status: 'approved' })}>{d.kpis.delivery.approved}</Link>} />
                <Stat label={t('portal.partners.content.dash.revision')} testId="kpi-delivery-revision" value={<Link to={listHref({ ...m, kind: 'delivery', status: 'revision_needed' })}>{d.kpis.delivery.revision}</Link>} />
                <Stat label={t('portal.partners.content.dash.overdue')} testId="kpi-delivery-overdue" value={<Link to={listHref({ ...m, kind: 'delivery', status: 'overdue' })}>{d.kpis.delivery.overdue}</Link>} />
                <Stat label={t('portal.partners.content.dash.atRisk')} testId="kpi-delivery-risk" value={<Link to={listHref({ ...m, kind: 'delivery', status: 'at_risk' })}>{d.kpis.delivery.atRisk}</Link>} />
              </div>
            </section>
            <section className="qc-panel" aria-labelledby="kpi-backlog">
              <Heading level={2} id="kpi-backlog">{t('portal.partners.content.dash.backlog')}</Heading>
              <div className="qx-grid">
                <Stat label={t('portal.partners.content.dash.open')} testId="kpi-backlog-open" value={<Link to={listHref({ backlog: 'true' })}>{d.kpis.backlog.open}</Link>} />
                <Stat label={t('portal.partners.content.dash.ready')} testId="kpi-backlog-ready" value={<Link to={listHref({ backlog: 'true', ready: 'true' })}>{d.kpis.backlog.ready}</Link>} />
                <Stat label={t('portal.partners.content.dash.notScheduled')} testId="kpi-backlog-unscheduled" value={<Link to={listHref({ backlog: 'true', bucket: 'not_scheduled' })}>{d.kpis.backlog.notScheduled}</Link>} />
                <Stat label={t('portal.partners.content.dash.cleared')} testId="kpi-backlog-cleared" value={<Link to={listHref({ clearedIn: month })}>{d.kpis.backlog.cleared}</Link>} />
              </div>
            </section>
            <section className="qc-panel" aria-labelledby="dash-byclient">
              <Heading level={2} id="dash-byclient">{t('portal.partners.content.dash.byClient')}</Heading>
              <DataTable rows={d.byClient} rowKey={(r) => r.clientId} empty={t('portal.partners.content.list.empty')} caption={t('portal.partners.content.dash.byClient')}
                columns={[
                  { key: 'client', header: t('portal.partners.content.col.client'), render: (r) => <Link to={listHref({ client: r.clientId, ...m })}>{r.clientName}</Link> },
                  { key: 'scope', header: t('portal.partners.content.dash.scope'), render: (r) => r.scope },
                  { key: 'planned', header: t('portal.partners.content.dash.planned'), render: (r) => <Link to={listHref({ client: r.clientId, ...m })}>{r.planned}</Link> },
                  { key: 'done', header: t('portal.partners.content.dash.done'), render: (r) => <Link to={listHref({ client: r.clientId, ...m, done: 'true' })}>{r.done}</Link> },
                  { key: 'overdue', header: t('portal.partners.content.dash.overdue'), render: (r) => <Link to={listHref({ client: r.clientId, ...m, status: 'overdue' })}>{r.overdue}</Link> },
                  { key: 'risk', header: t('portal.partners.content.dash.atRisk'), render: (r) => <Link to={listHref({ client: r.clientId, ...m, status: 'at_risk' })}>{r.atRisk}</Link> },
                  { key: 'pct', header: t('portal.partners.content.scope.pct'), render: (r) => (r.pctDone === null ? t('portal.partners.content.none') : t('portal.partners.content.percent', { n: r.pctDone })) },
                  { key: 'backlog', header: t('portal.partners.content.dash.open'), render: (r) => <Link to={listHref({ client: r.clientId, backlog: 'true' })}>{r.backlog}</Link>, narrow: 'hide' },
                ]} />
            </section>
            <section className="qc-panel" aria-labelledby="dash-byam">
              <Heading level={2} id="dash-byam">{t('portal.partners.content.dash.byAm')}</Heading>
              <DataTable rows={d.byAm} rowKey={(r) => r.amId} empty={t('portal.partners.content.list.empty')} caption={t('portal.partners.content.dash.byAm')}
                columns={[
                  { key: 'am', header: t('portal.partners.content.dash.am'), render: (r) => <Link to={listHref({ am: r.amId, ...m })}>{r.amName}</Link> },
                  { key: 'clients', header: t('portal.partners.content.dash.clients'), render: (r) => r.clients },
                  { key: 'scope', header: t('portal.partners.content.dash.scope'), render: (r) => r.scope },
                  { key: 'planned', header: t('portal.partners.content.dash.planned'), render: (r) => <Link to={listHref({ am: r.amId, ...m })}>{r.planned}</Link> },
                  { key: 'done', header: t('portal.partners.content.dash.done'), render: (r) => <Link to={listHref({ am: r.amId, ...m, done: 'true' })}>{r.done}</Link> },
                  { key: 'overdue', header: t('portal.partners.content.dash.overdue'), render: (r) => <Link to={listHref({ am: r.amId, ...m, status: 'overdue' })}>{r.overdue}</Link> },
                  { key: 'risk', header: t('portal.partners.content.dash.atRisk'), render: (r) => <Link to={listHref({ am: r.amId, ...m, status: 'at_risk' })}>{r.atRisk}</Link> },
                  { key: 'pct', header: t('portal.partners.content.scope.pct'), render: (r) => (r.pctDone === null ? t('portal.partners.content.none') : t('portal.partners.content.percent', { n: r.pctDone })) },
                ]} />
            </section>
            <section className="qc-panel" aria-labelledby="dash-byplatform">
              <Heading level={2} id="dash-byplatform">{t('portal.partners.content.dash.byPlatform')}</Heading>
              <DataTable rows={d.byPlatform} rowKey={(r) => r.platform} empty={t('portal.partners.content.list.empty')} caption={t('portal.partners.content.dash.byPlatform')}
                columns={[
                  { key: 'platform', header: t('portal.partners.content.filter.platform'), render: (r) => catalogLabel(base.catalog.platforms, r.platform) },
                  { key: 'planned', header: t('portal.partners.content.dash.planned'), render: (r) => <Link to={listHref({ platform: r.platform, ...m, kind: 'posting' })}>{r.planned}</Link> },
                  { key: 'posted', header: t('portal.partners.content.platformStatus.posted'), render: (r) => <Link to={listHref({ platform: r.platform, ...m, kind: 'posting', platformStatus: 'posted' })}>{r.posted}</Link> },
                  { key: 'missed', header: t('portal.partners.content.platformStatus.missed'), render: (r) => <Link to={listHref({ platform: r.platform, ...m, kind: 'posting', platformStatus: 'missed' })}>{r.missed}</Link> },
                  { key: 'pending', header: t('portal.partners.content.dash.pending'), render: (r) => r.pending },
                ]} />
            </section>
            <Text tone="muted">{t('portal.partners.content.dash.period', { month: fmtMonth(locale, month, true) })}</Text>
          </>
        )}
      </Loadable>
    </Screen>
  );
}

// ---------------------------------------------------------------- settings (admin)

type SettingsTab = 'clients' | 'team' | 'catalog' | 'rules' | 'import';

export function SettingsPage() {
  return <WithBase>{(b, reload) => <SettingsBody base={b} reloadBase={reload} />}</WithBase>;
}

function SettingsBody({ base, reloadBase }: { base: Base; reloadBase: () => void }) {
  const { t } = useSurface();
  const [tab, setTab] = useState<SettingsTab>('clients');
  if (!base.me.can.setup) {
    return (
      <Screen title={t('portal.partners.content.settings.title')} eyebrow={t('portal.partners.content.eyebrow')} testId="content-settings">
        <ContentNav me={base.me} />
        <Notice tone="warn">{t('portal.kit.forbidden')}</Notice>
      </Screen>
    );
  }
  const tabs = [
    { id: 'clients', label: t('portal.partners.content.settings.tab.clients') },
    { id: 'team', label: t('portal.partners.content.settings.tab.team') },
    { id: 'catalog', label: t('portal.partners.content.settings.tab.catalog') },
    { id: 'rules', label: t('portal.partners.content.settings.tab.rules') },
    { id: 'import', label: t('portal.partners.content.settings.tab.import') },
  ];
  return (
    <Screen title={t('portal.partners.content.settings.title')} subtitle={t('portal.partners.content.settings.subtitle')} eyebrow={t('portal.partners.content.eyebrow')} testId="content-settings">
      <ContentNav me={base.me} />
      <Tabs tabs={tabs} active={tab} onSelect={(id) => setTab(id as SettingsTab)} label={t('portal.partners.content.settings.tabs')}>
        {tab === 'clients' ? <ClientsSettings base={base} onChanged={reloadBase} /> : null}
        {tab === 'team' ? <TeamSettings base={base} onChanged={reloadBase} /> : null}
        {tab === 'catalog' ? <CatalogSettings base={base} onChanged={reloadBase} /> : null}
        {tab === 'rules' ? <RulesSettings /> : null}
        {tab === 'import' ? <ImportSettings base={base} /> : null}
      </Tabs>
    </Screen>
  );
}

/** Activate / deactivate a row. Items in use are deactivated, never deleted (US-03). */
function RowToggle({ active, run, onDone }: { active: boolean; run: () => Promise<unknown>; onDone: () => void }) {
  const { t } = useSurface();
  const act = useAction(run);
  return (
    <span className="qc-inline">
      <Button type="button" disabled={act.pending} onClick={async () => { if (await act.run()) onDone(); }}>
        {active ? t('portal.partners.content.settings.deactivate') : t('portal.partners.content.settings.activate')}
      </Button>
      <ContentError error={act.error} />
    </span>
  );
}

const statusText = (t: Translate, active: boolean) => t(active ? 'portal.partners.content.settings.active' : 'portal.partners.content.settings.inactive');

function ClientsSettings({ base, onChanged }: { base: Base; onChanged: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const platforms = base.catalog.platforms.filter((x) => x.active);
  const ams = base.members.filter((m) => m.active && m.role === 'account_manager');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<ClientKind>('posting');
  const [am, setAm] = useState(ams[0]?.id ?? '');
  const [picked, setPicked] = useState<string[]>(platforms.map((p) => p.key));
  const create = useAction(() => api.createClient({ name: name.trim(), kind, accountManagerId: am, ...(kind === 'posting' ? { platforms: picked } : {}) }));
  const ok = name.trim().length >= 2 && !!am;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    if (await create.run()) { setName(''); onChanged(); }
  };
  return (
    <>
      <DataTable rows={base.clients} rowKey={(c) => c.id} empty={t('portal.partners.content.list.empty')} caption={t('portal.partners.content.settings.clients')}
        columns={[
          { key: 'name', header: t('portal.partners.content.col.client'), render: (c) => c.name },
          { key: 'kind', header: t('portal.partners.content.new.kind'), render: (c) => t(`portal.partners.content.kind.${c.kind}`) },
          { key: 'am', header: t('portal.partners.content.dash.am'), render: (c) => base.members.find((m) => m.id === c.accountManagerId)?.name ?? t('portal.partners.content.none') },
          { key: 'platforms', header: t('portal.partners.content.filter.platform'), render: (c) => c.platforms.map((p) => catalogLabel(base.catalog.platforms, p)).join(', ') || t('portal.partners.content.none'), narrow: 'hide' },
          { key: 'status', header: t('portal.partners.content.settings.status'), render: (c) => <Badge tone={c.active ? 'good' : 'neutral'}>{statusText(t, c.active)}</Badge> },
          { key: 'sync', header: t('portal.partners.content.sync.column'), narrow: 'hide', render: (c) => (c.kind === 'posting' ? <SyncAccountPicker client={c} onDone={onChanged} /> : null) },
          { key: 'toggle', header: '', render: (c) => <RowToggle active={c.active} run={() => api.updateClient(c.id, { active: !c.active })} onDone={onChanged} /> },
        ]} />
      <section className="qc-panel" aria-labelledby="content-client-new">
        <Heading level={2} id="content-client-new">{t('portal.partners.content.settings.addClient')}</Heading>
        <form onSubmit={submit} noValidate>
        <div className="qc-form-grid">
          <Field label={t('portal.partners.content.settings.name')} error={name && name.trim().length < 2 ? t('portal.partners.content.new.required') : null}>
            {(id, d) => <input id={id} name="name" required maxLength={120} value={name} aria-describedby={d} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t('portal.partners.content.new.kind')}>
            {(id) => <select id={id} name="kind" value={kind} onChange={(e) => setKind(e.target.value as ClientKind)}>
              <option value="posting">{t('portal.partners.content.kind.posting')}</option>
              <option value="delivery">{t('portal.partners.content.kind.delivery')}</option>
            </select>}
          </Field>
          <Field label={t('portal.partners.content.settings.am')}>
            {(id) => <select id={id} name="am" value={am} onChange={(e) => setAm(e.target.value)}>
              {ams.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>}
          </Field>
        </div>
        {kind === 'posting' ? (
          <fieldset className="qc-fieldset">
            <legend>{t('portal.partners.content.new.platforms')}</legend>
            <div className="qc-checks">
              {platforms.map((p) => <Checkbox key={p.key} name="platforms" label={p.label} checked={picked.includes(p.key)}
                onChange={(on) => setPicked((x) => (on ? [...new Set([...x, p.key])] : x.filter((k) => k !== p.key)))} />)}
            </div>
          </fieldset>
        ) : null}
        <Button type="submit" variant="primary" disabled={!ok || create.pending}>{t('portal.partners.content.settings.addClient')}</Button>
        </form>
        <ContentError error={create.error} />
      </section>
    </>
  );
}

/** Client-side check only (the server validates and stores E.164). Spaces, dashes, dots and brackets are allowed. */
export function isWhatsAppNumber(v: string): boolean {
  return /^\+[1-9]\d{7,14}$/.test(v.replace(/[\s\-().]/g, ''));
}

export function TeamSettings({ base, onChanged }: { base: Base; onChanged: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [name, setName] = useState('');
  const [role, setRole] = useState<MemberRole>('designer');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [optIn, setOptIn] = useState(false);
  const create = useAction(() => api.createMember({
    name: name.trim(), role, ...(email.trim() ? { email: email.trim() } : {}),
    ...(phone.trim() ? { whatsappPhone: phone.trim(), whatsappOptIn: optIn } : {}),
  }));
  const ok = name.trim().length > 0 && whatsAppOk(phone, optIn);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    if (await create.run()) { setName(''); setEmail(''); setPhone(''); setOptIn(false); onChanged(); }
  };
  return (
    <>
      <DataTable rows={base.members} rowKey={(m) => m.id} empty={t('portal.partners.content.list.empty')} caption={t('portal.partners.content.settings.team')}
        columns={[
          { key: 'name', header: t('portal.partners.content.settings.name'), render: (m) => m.name },
          { key: 'role', header: t('portal.partners.content.settings.role'), render: (m) => t(`portal.partners.content.role.${m.role}`) },
          { key: 'email', header: t('portal.partners.content.settings.email'), render: (m) => m.email ?? t('portal.partners.content.none'), narrow: 'hide' },
          { key: 'whatsapp', header: t('portal.partners.content.settings.whatsapp.column'), narrow: 'hide', render: (m) => (m.whatsappPhone
            ? <span dir="ltr">{m.whatsappPhone} · {t(m.whatsappOptIn ? 'portal.partners.content.settings.whatsapp.on' : 'portal.partners.content.settings.whatsapp.off')}</span>
            : t('portal.partners.content.none')) },
          { key: 'status', header: t('portal.partners.content.settings.status'), render: (m) => <Badge tone={m.active ? 'good' : 'neutral'}>{statusText(t, m.active)}</Badge> },
          { key: 'toggle', header: '', render: (m) => <RowToggle active={m.active} run={() => api.updateMember(m.id, { active: !m.active })} onDone={onChanged} /> },
        ]} />
      <section className="qc-panel" aria-labelledby="content-team-new">
        <Heading level={2} id="content-team-new">{t('portal.partners.content.settings.addMember')}</Heading>
        <form onSubmit={submit} noValidate className="qc-form-grid">
          <Field label={t('portal.partners.content.settings.name')}>
            {(id) => <input id={id} name="name" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t('portal.partners.content.settings.role')}>
            {(id) => <select id={id} name="role" value={role} onChange={(e) => setRole(e.target.value as MemberRole)}>
              {MEMBER_ROLES.map((r) => <option key={r} value={r}>{t(`portal.partners.content.role.${r}`)}</option>)}
            </select>}
          </Field>
          <Field label={t('portal.partners.content.settings.email')} hint={t('portal.partners.content.settings.emailHint')}>
            {(id) => <input id={id} type="email" name="email" maxLength={254} value={email} onChange={(e) => setEmail(e.target.value)} />}
          </Field>
          <WhatsAppFields phone={phone} optIn={optIn} onPhone={(v) => { setPhone(v); if (!v.trim()) setOptIn(false); }} onOptIn={setOptIn} />
          <div><Button type="submit" variant="primary" disabled={!ok || create.pending}>{t('portal.partners.content.settings.addMember')}</Button></div>
        </form>
        <ContentError error={create.error} />
      </section>
      <MemberWhatsAppForm base={base} onChanged={onChanged} />
    </>
  );
}

/** Opt-in needs a valid number; an empty number cannot be opted in. */
const whatsAppOk = (phone: string, optIn: boolean) => (phone.trim() === '' || isWhatsAppNumber(phone)) && (!optIn || phone.trim() !== '');

/** WhatsApp number and opt-in fields (US-30). Admins only; the server is the authority on the format. */
function WhatsAppFields({ phone, optIn, onPhone, onOptIn }: { phone: string; optIn: boolean; onPhone: (v: string) => void; onOptIn: (v: boolean) => void }) {
  const { t } = useSurface();
  const bad = phone.trim() !== '' && !isWhatsAppNumber(phone);
  return (
    <>
      <Field label={t('portal.partners.content.settings.whatsapp.phone')} hint={t('portal.partners.content.settings.whatsapp.phoneHint')}
        error={bad ? t('portal.partners.content.settings.whatsapp.phoneInvalid') : null}>
        {(id, describedBy) => <input id={id} type="tel" name="whatsappPhone" inputMode="tel" autoComplete="off" dir="ltr" maxLength={40}
          placeholder="+971 50 123 4567" value={phone} aria-describedby={describedBy} onChange={(e) => onPhone(e.target.value)} />}
      </Field>
      <Checkbox name="whatsappOptIn" label={t('portal.partners.content.settings.whatsapp.optIn')} checked={optIn} disabled={phone.trim() === ''} onChange={onOptIn} />
    </>
  );
}

/** Set the WhatsApp number and opt-in of one active team member (admins). */
function MemberWhatsAppForm({ base, onChanged }: { base: Base; onChanged: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const members = base.members.filter((m) => m.active);
  const [memberId, setMemberId] = useState(members[0]?.id ?? '');
  const first = members.find((m) => m.id === memberId);
  const [phone, setPhone] = useState(first?.whatsappPhone ?? '');
  const [optIn, setOptIn] = useState(first?.whatsappOptIn ?? false);
  const [language, setLanguage] = useState<WhatsAppLanguage>(first?.whatsappLanguage ?? 'en');
  const pick = (id: string) => {
    const m = members.find((x) => x.id === id);
    setMemberId(id);
    setPhone(m?.whatsappPhone ?? '');
    setOptIn(m?.whatsappOptIn ?? false);
    setLanguage(m?.whatsappLanguage ?? 'en');
  };
  const save = useAction(() => api.updateMember(memberId, { whatsappPhone: phone.trim() ? phone.trim() : null, whatsappOptIn: phone.trim() ? optIn : false, whatsappLanguage: language }));
  const ok = !!first && whatsAppOk(phone, optIn);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (ok && await save.run()) onChanged();
  };
  if (members.length === 0) return null;
  return (
    <section className="qc-panel" aria-labelledby="content-whatsapp-member">
      <Heading level={2} id="content-whatsapp-member">{t('portal.partners.content.settings.whatsapp.memberHeading')}</Heading>
      <form onSubmit={submit} noValidate className="qc-form-grid">
        <Field label={t('portal.partners.content.settings.whatsapp.member')}>
          {(id) => <select id={id} name="member" value={memberId} onChange={(e) => pick(e.target.value)}>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>}
        </Field>
        <WhatsAppFields phone={phone} optIn={optIn} onPhone={(v) => { setPhone(v); if (!v.trim()) setOptIn(false); }} onOptIn={setOptIn} />
        <WhatsAppLanguageField value={language} onChange={setLanguage} />
        <div><Button type="submit" variant="primary" disabled={!ok || save.pending}>{t('portal.partners.content.save')}</Button></div>
      </form>
      <ContentError error={save.error} />
      {save.result ? <Notice testId="member-whatsapp-saved">{t('portal.partners.content.settings.whatsapp.memberSaved')}</Notice> : null}
    </section>
  );
}

function CatalogSettings({ base, onChanged }: { base: Base; onChanged: () => void }) {
  const { t } = useSurface();
  return (
    <>
      <CatalogGroup kind="platform" title={t('portal.partners.content.settings.platforms')} rows={base.catalog.platforms} onChanged={onChanged} />
      <CatalogGroup kind="content_type" title={t('portal.partners.content.settings.contentTypes')} rows={base.catalog.contentTypes} onChanged={onChanged} />
      <CatalogGroup kind="stage" title={t('portal.partners.content.settings.stages')} rows={base.catalog.stages} onChanged={onChanged} />
    </>
  );
}

function CatalogGroup({ kind, title, rows, onChanged }: { kind: 'platform' | 'content_type' | 'stage'; title: string; rows: readonly CatalogRow[]; onChanged: () => void }) {
  const { t } = useSurface();
  const api = useApi();
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [early, setEarly] = useState(false);
  const [ready, setReady] = useState(false);
  const create = useAction(() => api.createCatalog({ kind, key: key.trim(), label: label.trim(), ...(kind === 'stage' ? { isEarly: early, isReady: ready } : {}) }));
  const keyOk = /^[a-z0-9_]{1,40}$/.test(key.trim());
  const ok = keyOk && label.trim().length > 0;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ok) return;
    if (await create.run()) { setKey(''); setLabel(''); setEarly(false); setReady(false); onChanged(); }
  };
  return (
    <section className="qc-panel" aria-label={title}>
      <Heading level={2}>{title}</Heading>
      <DataTable rows={rows} rowKey={(r) => r.id} empty={t('portal.partners.content.list.empty')} caption={title}
        columns={[
          { key: 'label', header: t('portal.partners.content.settings.label'), render: (r) => r.label },
          { key: 'key', header: t('portal.partners.content.settings.key'), render: (r) => <code>{r.key}</code>, narrow: 'hide' },
          ...(kind === 'stage' ? [
            { key: 'early', header: t('portal.partners.content.settings.early'), render: (r: CatalogRow) => (r.isEarly ? t('portal.partners.content.settings.yes') : t('portal.partners.content.settings.no')), narrow: 'hide' as const },
            { key: 'ready', header: t('portal.partners.content.settings.ready'), render: (r: CatalogRow) => (r.isReady ? t('portal.partners.content.settings.yes') : t('portal.partners.content.settings.no')), narrow: 'hide' as const },
          ] : []),
          { key: 'status', header: t('portal.partners.content.settings.status'), render: (r) => <Badge tone={r.active ? 'good' : 'neutral'}>{statusText(t, r.active)}</Badge> },
          { key: 'toggle', header: '', render: (r) => <RowToggle active={r.active} run={() => api.updateCatalog(r.id, { active: !r.active })} onDone={onChanged} /> },
        ]} />
      <form onSubmit={submit} noValidate className="qc-form-grid">
        <Field label={t('portal.partners.content.settings.key')} hint={t('portal.partners.content.settings.keyHint')} error={key && !keyOk ? t('portal.partners.content.settings.keyInvalid') : null}>
          {(id, d) => <input id={id} name="key" maxLength={40} value={key} aria-describedby={d} onChange={(e) => setKey(e.target.value)} />}
        </Field>
        <Field label={t('portal.partners.content.settings.label')}>
          {(id) => <input id={id} name="label" maxLength={60} value={label} onChange={(e) => setLabel(e.target.value)} />}
        </Field>
        {kind === 'stage' ? (
          <div>
            <Checkbox name="isEarly" label={t('portal.partners.content.settings.earlyHint')} checked={early} onChange={setEarly} />
            <Checkbox name="isReady" label={t('portal.partners.content.settings.readyHint')} checked={ready} onChange={setReady} />
          </div>
        ) : null}
        <div><Button type="submit" disabled={!ok || create.pending}>{t('portal.partners.content.settings.add')}</Button></div>
      </form>
      <ContentError error={create.error} />
    </section>
  );
}

export function RulesSettings() {
  const { t } = useSurface();
  const api = useApi();
  const current = useResource<Settings>('content:settings', () => api.settings());
  return (
    <Loadable resource={current}>
      {(s) => (
        <>
          <RulesForm key={`${s.atRiskDays}:${s.digestHour}`} initial={s} onSaved={() => current.reload()} t={t} />
          <WhatsAppToggleForm key={`wa:${s.whatsappEnabled}:${s.whatsappAvailable ?? ''}`} initial={s} onSaved={() => current.reload()} t={t} />
        </>
      )}
    </Loadable>
  );
}

/** Agency toggle for WhatsApp notifications (US-30). Off by default; members still need their own opt-in. */
function WhatsAppToggleForm({ initial, onSaved, t }: { initial: Settings; onSaved: () => void; t: Translate }) {
  const api = useApi();
  const [on, setOn] = useState(initial.whatsappEnabled);
  const save = useAction(() => api.saveSettings({ whatsappEnabled: on }));
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await save.run()) onSaved();
  };
  return (
    <section className="qc-panel" aria-labelledby="content-whatsapp">
      <Heading level={2} id="content-whatsapp">{t('portal.partners.content.settings.whatsapp.heading')}</Heading>
      <Text tone="muted">{t('portal.partners.content.settings.whatsapp.hint')}</Text>
      {initial.whatsappAvailable === false ? <Notice tone="warn">{t('portal.partners.content.settings.whatsapp.unavailable')}</Notice> : null}
      <form onSubmit={submit} className="qc-form-grid">
        <Checkbox name="whatsappEnabled" label={t('portal.partners.content.settings.whatsapp.toggle')} checked={on} onChange={setOn} />
        <div><Button type="submit" variant="primary" disabled={save.pending}>{t('portal.partners.content.save')}</Button></div>
      </form>
      <ContentError error={save.error} />
      {save.result ? <Notice testId="whatsapp-settings-saved">{t('portal.partners.content.settings.whatsapp.saved')}</Notice> : null}
    </section>
  );
}

function RulesForm({ initial, onSaved, t }: { initial: Settings; onSaved: () => void; t: Translate }) {
  const api = useApi();
  const [atRisk, setAtRisk] = useState(initial.atRiskDays);
  const [digest, setDigest] = useState(initial.digestHour);
  const save = useAction(() => api.saveSettings({ atRiskDays: atRisk, digestHour: digest }));
  const ok = Number.isInteger(atRisk) && atRisk >= 0 && atRisk <= 30 && Number.isInteger(digest) && digest >= 0 && digest <= 23;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (ok && await save.run()) onSaved();
  };
  return (
    <section className="qc-panel" aria-labelledby="content-rules">
      <Heading level={2} id="content-rules">{t('portal.partners.content.settings.rules')}</Heading>
      <form onSubmit={submit} className="qc-form-grid">
        <Field label={t('portal.partners.content.settings.atRiskDays')} hint={t('portal.partners.content.settings.atRiskHint')}>
          {(id) => <input id={id} type="number" min={0} max={30} step={1} value={atRisk} onChange={(e) => setAtRisk(Number(e.target.value))} />}
        </Field>
        <Field label={t('portal.partners.content.settings.digestHour')} hint={t('portal.partners.content.settings.digestHint')}>
          {(id) => <input id={id} type="number" min={0} max={23} step={1} value={digest} onChange={(e) => setDigest(Number(e.target.value))} />}
        </Field>
        <div><Button type="submit" variant="primary" disabled={!ok || save.pending}>{t('portal.partners.content.save')}</Button></div>
      </form>
      <ContentError error={save.error} />
      {save.result ? <Notice testId="settings-saved">{t('portal.partners.content.settings.saved')}</Notice> : null}
    </section>
  );
}

const SHEETS = [
  ['setup', 'portal.partners.content.import.sheet.setup'],
  ['postingLog', 'portal.partners.content.import.sheet.postingLog'],
  ['deliveryLog', 'portal.partners.content.import.sheet.deliveryLog'],
  ['monthlyScope', 'portal.partners.content.import.sheet.monthlyScope'],
] as const;
type Sheet = (typeof SHEETS)[number][0];

/** Excel import (US-32): pick a CSV per tab, parse in the browser, dry run, review, then commit. */
function ImportSettings({ base }: { base: Base }) {
  const { t } = useSurface();
  const api = useApi();
  const [rows, setRows] = useState<Record<Sheet, Record<string, string>[]>>({ setup: [], postingLog: [], deliveryLog: [], monthlyScope: [] });
  const [names, setNames] = useState<Partial<Record<Sheet, string>>>({});
  const [dry, setDry] = useState<ImportReport | null>(null);
  const [done, setDone] = useState<ImportReport | null>(null);
  const run = useAction((dryRun: boolean) => {
    const body: ImportBody = { dryRun, setup: rows.setup, postingLog: rows.postingLog, deliveryLog: rows.deliveryLog, monthlyScope: rows.monthlyScope };
    return api.importData(body, dryRun ? undefined : newIdempotencyKey());
  });
  const pick = async (sheet: Sheet, file: File | undefined) => {
    setDry(null); setDone(null);
    if (!file) return;
    const parsed = parseCsv(await file.text());
    setRows((r) => ({ ...r, [sheet]: parsed }));
    setNames((n) => ({ ...n, [sheet]: file.name }));
  };
  const dryRunClick = async () => { const r = await run.run(true); if (r) { setDry(r); setDone(null); } };
  const commitClick = async () => { const r = await run.run(false); if (r) setDone(r); };
  const total = SHEETS.reduce((a, [k]) => a + rows[k].length, 0);
  const report = done ?? dry;
  return (
    <section className="qc-panel" aria-labelledby="content-import" data-testid="content-import">
      <Heading level={2} id="content-import">{t('portal.partners.content.import.title')}</Heading>
      <Text tone="muted">{t('portal.partners.content.import.hint')}</Text>
      <div className="qc-form-grid">
        {SHEETS.map(([k, labelKey]) => (
          <Field key={k} label={t(labelKey)} hint={names[k] ? t('portal.partners.content.import.rows', { n: rows[k].length, name: names[k] ?? '' }) : undefined}>
            {(id, d) => <input id={id} type="file" accept=".csv,text/csv" aria-describedby={d} onChange={(e) => void pick(k, e.target.files?.[0])} />}
          </Field>
        ))}
      </div>
      <div className="qx-screen__actions">
        <Button type="button" disabled={total === 0 || run.pending} onClick={() => void dryRunClick()}>{t('portal.partners.content.import.dryRun')}</Button>
        <Button type="button" variant="primary" disabled={!dry || dry.errors.length > 0 || !!done || run.pending} onClick={() => void commitClick()}>{t('portal.partners.content.import.commit')}</Button>
      </div>
      <ContentError error={run.error} />
      {report ? (
        <div data-testid="import-report">
          <Heading level={3}>{t('portal.partners.content.import.report')}</Heading>
          <ul className="qc-list">
            <li>{t('portal.partners.content.import.counts', { clients: report.counts.clients, members: report.counts.members, scope: report.counts.scope, deliverables: report.counts.deliverables, skipped: report.counts.skippedDuplicates })}</li>
          </ul>
          {report.committed ? <Notice tone="good" testId="import-committed">{t('portal.partners.content.import.committed')}</Notice> : null}
          {report.errors.length > 0 ? (
            <DataTable rows={report.errors} rowKey={(e) => `${e.sheet}:${e.row}:${e.message}`} empty="" caption={t('portal.partners.content.import.errors')}
              columns={[
                { key: 'sheet', header: t('portal.partners.content.import.sheet'), render: (e) => e.sheet },
                { key: 'row', header: t('portal.partners.content.import.row'), render: (e) => e.row },
                { key: 'message', header: t('portal.partners.content.import.message'), render: (e) => e.message },
              ]} />
          ) : <Text tone="muted">{t('portal.partners.content.import.noErrors')}</Text>}
          {report.duplicates.length > 0 ? (
            <DataTable rows={report.duplicates} rowKey={(e) => `${e.sheet}:${e.row}:${e.key}`} empty="" caption={t('portal.partners.content.import.duplicates')}
              columns={[
                { key: 'sheet', header: t('portal.partners.content.import.sheet'), render: (e) => e.sheet },
                { key: 'row', header: t('portal.partners.content.import.row'), render: (e) => e.row },
                { key: 'key', header: t('portal.partners.content.import.key'), render: (e) => e.key },
              ]} />
          ) : null}
        </div>
      ) : null}
      <Text tone="muted">{t('portal.partners.content.import.teamHint', { n: base.members.length })}</Text>
    </section>
  );
}
